import { type RollSource, type RecordedDraw } from './roll-source.js';
import { ignoresDamageTypeResistance } from '../ability/rolls/resistance-ignore.js';
import { statusPreventsActions } from './status-action-gate.js';
import { applyRawDiceModifiers } from '../raw-dice-modifiers.js';
import { savedAttackMembers } from './attack-members.js';
import { strayCandidates, pickStrayCandidate } from '../ability/stray-shot.js';
import type { TurnDeathResult, TurnDeathTransition } from '../turns/end-turn.js';
import { ecLean, HandBack, Refusal } from '../ability/context.js';
import { attackCriticalHitThreshold, hinderedAttackStacksFromCounter, targetDamageReduction, type WeaponAttackSpatialContext } from '../ability/templates/template-weapon-attack-roll.js';
import { targetIsAutoCriticalForAttackRoll } from '../port/k3-rules/damage_roll_attack_helpers.js';
import { catalogVariable } from '../port/k3-rules/catalog.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { rollTypeItemStacks } from '../ability/rolls/roll-type-roll-shared';
import type { DamageReduction } from '../ability/rolls/reduce-damage-status.js';
import { temporaryVitality } from '../ability/rolls/temporary-vitality.js';
import { nextRollAgainstRider } from '../ability/skill-roll.js';
import { statusScalar } from './activity.js';
import { statusItemId, statusRows } from '../statuses/expiry.js';
import { shieldedChanges } from '../statuses/shielded.js';
import { ENGINE_FIST_MARKER_ID } from '../../shared/paired-limb-forms.js';
import { rollWithStacks, criticalHitBonus, criticalHitDamageMultiplier, critRolledDiceTarget, explosionChain, type CritVariant } from './weapon-attack-dice.js';
import { resolveCritCascades, type CritCascadeRules, type CritCascadeTrigger } from './weapon-attack-cascades.js';
import { ATTACKS_AGAINST_CANNOT_GAIN_ADV, ATTACKS_AGAINST_CANNOT_GAIN_ADV_LABEL, clampAttackAdvAgainst } from '../ability/rolls/attacks-against-cannot-gain-adv.js';
import { afterMaimedAscension, maimedEvadeSource, resolveMaimedSave, type FortitudeSaveInput, type MaimedSave } from './weapon-attack-maimed.js';
import { damageTypesRolledAtMax } from '../ability/caster-spell-layers.js';
import type { AbilityPreconditionReaders } from './ability-preconditions.js';
import { passesDbGate } from '../reactions/trigger-dispatch.js';
import {
  planExtraDamage, rollExtraFragment, totalDamage, mitigateFragment,
  type ExtraDamageFragment, type ExtraDamagePlan, type DamageAdjustment, type DamageFeedback, type ExtraDamageReaders,
} from './weapon-attack-extra-damage.js';
export { type ExtraDamageFragment, type DamageAdjustment, type DamageFeedback } from './weapon-attack-extra-damage.js';

export { replayRollSource, recordingRollSource, browserRollSource, type RollSource, type RecordedDraw } from './roll-source.js';
export { rollWithStacks, explosionChain, criticalHitBonus, critRolledDiceCount, critRolledDiceTarget, explosionThresholdForLink } from './weapon-attack-dice.js';

type Row = Record<string, unknown>;

export const WEAPON_ATTACK_SCHEMA = 'janus.action.attack.v2' as const;

/** Universally granted reactions (`finalize_creature_arr` ~line 920) — on EVERY encounter creature. */
export const EVADE_ID = 'BE4CCA95-33F0-323D-BA54-844D2540C0F0';
export const OPPORTUNITY_ATTACK_ID = 'D1EADC34-832C-1909-2FC3-722AC857FBEA';
/** Native weapon attack roll types. Melee-type weapons retain melee rolls when thrown. */
export const WEAPON_MELEE_ATTACK_ROLL = '2FE8DFB7-FE61-4862-AF71-3523262350A0';
export const WEAPON_RANGED_ATTACK_ROLL = '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';
/** EquipmentSlot 'head' and WeaponClassification 'natural_weapon'. The item stores the catalog UUID (the
 *  browser's definition carries `3805BDD7-…` / `AB7DEAAE-…`); PHP's layer_value hands the same UUID back.
 *  The variable names are accepted too so a test fixture can stay readable. (Packet attempt 2, 2026-09-08:
 *  the string-only compare declined the real Bite.) */
const EQUIPMENT_SLOT_HEAD = new Set(['head', '3805BDD7-3461-DDDB-F445-922A5A992987']);
/** EquipmentSlot 'main_hand' / 'off_hand' — where a free-hand Slam or a claw sits. */
const EQUIPMENT_SLOT_HAND = new Set(['main_hand', 'off_hand', 'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD', '4FF85144-67EC-9D87-AE28-15137A1A2481']);
const NATURAL_WEAPON = new Set(['natural_weapon', 'AB7DEAAE-62BF-4996-A510-5F2B192F8F14']);
const catalogKey = (value: unknown): string => { const s = String(scalar(value) ?? '').trim(); return /^[0-9a-f]{8}-/i.test(s) ? s.toUpperCase() : s; };
/** A weapon / natural_weapon / attack_ability / quick_ability costs 1 AP (check_caster_aps_and_in_combat §4). */
const ATTACK_AP_COST = 1;
/** Every layer the catalog Evade carries today (probe 2026-09-08). The resolver models exactly ONE condition of it —
 *  `if_caster_has_status_effect_not_active` (Grappled / Immobile / Paralyzed / Restrained: the target cannot Evade while
 *  held, check_ability_conditions.php:549) — so a definition carrying any OTHER condition layer hands back pre-draw.
 *  Found by the S6 mitigation packet: legacy never let the Immobile dummy creature Evade, while this lane spent its
 *  reaction and rolled the disadvantage — a divergence the S5 kill golden had already carried (BUG LEDGER 09-08). */
const EVADE_KNOWN_LAYERS = new Set(['range', 'effect_ends_on_start_of_caster_next_turn', 'if_caster_has_status_effect_not_active',
  'triggers_on_roll_type_against_the_caster', 'roll_type_against_dis', 'triggers_on_next_roll_type_against']);
/** The vitality-status catalog ids: a forbidden status that is one of these is judged from the vitality cluster, not
 *  the status rows (check_ability_conditions.php `$v_status_ids`) — unmodelled here, so it hands back. */
const VITALITY_STATUS_IDS = new Set(['82788F70-EBEF-44C4-80E7-6622AD20DD60', 'FF30B76E-785D-2CAF-FC66-C131D0548808', '658483AA-34DE-74E5-C974-E3E202C31CC7',
  'C97076EF-FED9-19B4-D109-C22F9BC740E8', '44B8792E-632E-2651-C666-67DFD181E92A', '298BE1DD-2917-476E-D79C-92CF8C598926', 'D5DE14C4-3194-87BC-D767-6D45BDD90552', '8AE129AF-52D8-CCE8-706A-85767DB52740']);
/** Damage-type defence layers, in the order check_damage_resistance_and_immunity.php applies them (S6, 2026-09-08).
 *  The compile relocates the authored root layers into computed.creature.defenses.{resistance,immune,vulnerable};
 *  both readers (PHP layer_entries / ftLayerEntries) bridge that store, proven by a browser probe on the dummy
 *  creature carrying a resistance (tools/test/_a2_s6_mitigation_gate_probe.mjs, step 0 of the S6 brief). */
export const MITIGATION_LAYERS = [
  { layer: 'damage_type_resistance', kind: 'resistance' },
  { layer: 'damage_type_immunity', kind: 'immunity' },
  { layer: 'damage_type_vulnerability', kind: 'vulnerability' },
] as const;
export type MitigationKind = (typeof MITIGATION_LAYERS)[number]['kind'];
/** The object-typed defence family (encounter inventory / props, folded from item_composition) runs the same loop
 *  a second time in PHP. A creature target carrying any of it is handed back — pre-draw, on state. */
const OBJECT_MITIGATION_LAYERS = ['object_damage_type_resistance', 'object_damage_type_immunity', 'object_damage_type_vulnerability', 'item_composition'] as const;
/** Perfect Breach (2026-10-07): ALL damage the holder deals skips (object) Damage Mitigation — ft_damage_ignores_all_damage_mitigation(). */
const DAMAGE_IGNORES_ALL_MITIGATION = 'damage_ignores_all_damage_mitigation';
/** damage_type_roll.php's other result modifiers — rolled-at-max/min, doubled, halved — on the caster (statuses /
 *  passives), on the target (the `against` mirror) or on the ability. Resolved by PHP helpers that also walk statuses,
 *  so this direct-entry gate is a best effort: an AUTHORED layer hands back; a status-carried one is a recorded residual
 *  (BUG LEDGER 09-08, found reading the mitigation call site). */
const DAMAGE_RESULT_MODIFIER_LAYERS = [
  'roll_type_is_rolled_at_max', 'roll_type_is_rolled_at_min', 'roll_type_roll_result_is_doubled', 'roll_type_roll_result_is_halved',
  'roll_type_against_is_rolled_at_max', 'roll_type_against_is_rolled_at_min', 'roll_type_against_roll_result_is_doubled', 'roll_type_against_roll_result_is_halved',
  // Creature-type-scoped forced max on the fragment's own source (damage_type_roll.php, 2026-09-14) — unported.
  'maximum_damage_against_creature_type',
] as const;

/** One mitigation step the damage fragment took, as check_damage_resistance_and_immunity.php records it: the kind, the
 *  layer it came from, and the `$defense_meta` payload (kind, defense_layer, damage_type_id, then the first-wins
 *  source_* fields of the matched entry, nulls and blanks stripped, in PHP's key order). The commit builder turns each
 *  into the log tuple (`0.5 Ω` / `θ` / `2 λ`). */
export interface MitigationStep {
  kind: MitigationKind;
  layer: string;
  meta: Record<string, unknown>;
  /** A matched resistance the attacker's ignore_damage_type_resistance lists: logged "<type> Resistance Ignored" (ρ), not applied. */
  ignored?: true;
}

/** First-wins match of a defence entry against the rolled damage type — a structured `{damage_type, source_*}` entry
 *  (add_damage_type_defense_entry) or a legacy scalar UUID. PHP compares the RAW strings with `===` (case-sensitive), so
 *  this does too: the rolled id is the weapon layer's value as stored, never normalised (Codex S6 review F1 — an
 *  upper-cased compare would match an entry PHP skips, or attribute the wrong first source). */
export function matchDefenseEntry(entries: unknown[], damageTypeId: string): Row | null {
  for (const entry of entries) {
    const row = record(entry);
    if (row) { if (row.damage_type === damageTypeId) return row; }
    else if (scalar(entry) === damageTypeId) return { damage_type: damageTypeId };
  }
  return null;
}
/** layer_list_entry_uuid twin for the caster's `ignore_damage_type_resistance` list. */
export function entryUuid(entry: unknown): string {
  return upper(readLayerListEntryUuid(entry) || record(entry)?.damage_type || '');
}
/** Build the `$defense_meta` of one matched entry, in PHP's key order, nulls / blanks stripped. */
export function defenseMeta(kind: MitigationKind, layer: string, damageTypeId: string, entry: Row): Record<string, unknown> {
  const meta: Record<string, unknown> = { kind, defense_layer: layer, damage_type_id: damageTypeId };
  for (const key of ['source_item_id', 'source_name', 'source_kind', 'source_origin_item_id', 'source_origin_name'] as const) {
    const value = entry[key];
    if (value !== null && value !== undefined && value !== '') meta[key] = value;
  }
  return meta;
}
/** Apply the matched steps to a fragment result exactly as PHP does: floor-halve, zero, double — in order — then the
 *  fragment floor at 0 (`max(0, (int) …)`, damage_type_roll.php:1057). Exported so the commit test can pin it. */
export function applyMitigation(result: number, steps: readonly MitigationStep[]): number {
  let value = result;
  for (const step of steps) {
    if (step.ignored) continue;
    if (step.kind === 'resistance') value = Math.floor(value / 2);
    else if (step.kind === 'immunity') value = 0;
    else value *= 2;
  }
  return Math.max(0, Math.trunc(value));
}

export interface WeaponAttackPartner { weaponId: string; weapon: Row; label: 'off-hand' | 'main-hand'; explicitInventoryWeapon?: boolean }
export interface WeaponAttackReaders {
  partnerWeapon?: (weapon: Row, attacker: Row, target: Row, paidHand?: 'main' | 'off') => WeaponAttackPartner | { reason: string } | null;
  weaponEntries?: (weapon: Row, row: Row, key: string) => unknown[];
  /** Actual per-hit settlement between compound swings. Returns the shared frame's final state. */
  settleSwing?: (hit: { hit: boolean; paid: boolean; hasRider: boolean; targetId: string; rollType: string; postRollFinal: boolean; weaponId: string; damageTypes: string[]; damage: number; critical: boolean;
    casterHealing: number; vitality: number; luck: number; reaction: number; targetReaction: number; aps: number; before: number; nextRollAgainstConsumed: Array<{ targetId: string; statusId: string }>; maimedSave?: MaimedSave | null }) => {
      vitality: number; luck: number; targetReaction: number; targetFortunate?: boolean; stop: boolean; death: WeaponAttackOutcome['death'] };
  swingRollSources?: (target: Row, weaponId: string, rollType: string, rider: boolean) => AdvDisSource[];
  /** The enclosing shared frame folds live status modifiers on the first swing as well as a rider. */
  statusRollSources?: true;
  /** State-only damage reaction gate after the pre-roll Evade decision; never predicts a kill. */
  damageReactionPreflight?: (attacker: Row, target: Row, evadeSpent: boolean, damageTypes: string[], form?: { reverts: boolean; vitalityDamage: boolean }) => string | null;
  /** Page-held spatial facts, shared with the weapon template. OAs retain their movement-owned context. */
  attackSpatial?: (attacker: Row, target: Row, weapon: Row, options: { melee: boolean; throwing: boolean }) => WeaponAttackSpatialContext;
  /** The enclosing reaction frame previews before the d20, then applies hit-only reduction before damage dice. */
  postRollPreDraw?: (attacker: Row, target: Row, rollType: string) => void;
  postRollReduce?: (attacker: Row, target: Row, rollType: string) => { reductions: DamageReduction[]; reaction: number; vitality?: number; attackerLuck?: number };
  /** The known hit's positive incoming fragments, before untyped mitigation and vitality. */
  preMitigation?: (attacker: Row, target: Row, damageTypes: string[]) => {
    damageMitigation: number; reaction: number; reductions: DamageReduction[]; vitality: number; attackerLuck: number };
  /** The enclosing commit owns form removal, loss listeners and inventory restoration before vitality overflow. */
  absorbFormDamage?: (target: Row, damage: number) => number;
  strayTargetPreflight?: (target: Row) => string | null;
  creatureField?: (row: Row, key: string) => unknown;
  encounterField?: (row: Row, key: string) => unknown;
  turnDeath?: (encounter: Row, members: ReadonlyMap<string, Row>, dyingId: string) => TurnDeathResult;
  opposingTeams?: (attacker: Row, target: Row) => boolean;
  /** Universal Fortunate gate. `gained` reads an actual post-save gain instead of predicting room under the luck cap. */
  fortunateOnGain?: (row: Row, gained?: boolean) => boolean;
  /** Apply the paid swing's automatic grant in the shared frame before the other hand rolls. */
  applyInterSwingFortunate?: (luckAfterGain: number) => void;
  /** OWNER RULING 2026-10-02 (optional triggers are prompt-then-resolve — lane A11): the TARGET's pre-roll Evade (a reaction it spends) on a
   *  player-controlled target fires only on its controller's yes. Asked ONLY when Evade would fire (state, before any draw): true = Evade,
   *  false = declined (the swing rolls without it, the reaction stays), undefined = not asked yet → hand back `preflight:trigger-consent`
   *  (the page asks, ≤ 5 s, and resolves again). Absent (the census, an NPC page) = PHP's auto-fire. */
  evadeConsent?: (target: Row) => boolean | undefined;
  /** ftLayerValue twin. */
  value: (row: Row, name: string) => unknown;
  /** ftLayerEntries twin. */
  entries: (row: Row, name: string) => unknown[];
  /** Full definition {items, item_layer_types} for a catalog id (row catalog → object cache). */
  definition: (id: string) => Row | null;
  /** S10: a definition as seen from a SPECIFIC row (its own catalog first) — the target's passives live in the target's catalog.
   *  Falls back to `definition`. A layerless status_effect item counts as a definition. */
  definitionOn?: (row: Row, id: string) => Row | null;
  /** S10: the row's active status rows (`statuses.target`). Falls back to `row.statuses.target`. */
  statusRows?: (row: Row) => Row[];
  /** The page's existing possessor scene answers status-passive conditions. */
  conditionScene?: (row: Row) => ReturnType<NonNullable<ExtraDamageReaders['conditionScene']>>;
  /** PER-CREATURE combat membership (creatureIsInActiveCombat twin) — the attacker and the target can
   *  legitimately differ, and the reaction gate reads the REACTING creature's membership. */
  inCombat: (encounter: Row, row: Row) => boolean;
  /** `computed.creature.ac.ac.total` — the DC. NOT on the row; see the S1 census. */
  armorClass: (row: Row) => number | null;
  /** The attacker's trained tier die for the weapon's skill, or null when untrained (no tier draw). */
  skillTierDie: (row: Row, weapon: Row) => number | null;
  /** The compiled skill entry's `tier_level` (compute/_base.php: `is_flawed` ⇔ tier 0 → the UNTRAINED disadvantage,
   *  skill_roll_inc.php:239), or null when the attacker has NO compiled entry for the weapon's skill (unknown, hands back). */
  skillTierLevel: (row: Row, weapon: Row) => number | null;
  /** The compiled skill entry's adv/dis stacks, including global all-skill sources. */
  skillAdvDisStacks: (row: Row, weapon: Row) => number;
  /** Attribution for the same compiled stacks; OA logs split global layers from skill_base. */
  skillAdvDisSources?: (row: Row, weapon: Row) => AdvDisSource[];
  /** The ACTIVE status catalog ids on a row (creature_statuses(row,'target') → item_id), upper-cased — for an ability's
   *  `if_caster_has_status_effect_not_active` gate (check_ability_conditions.php:549). */
  statusItemIds: (row: Row) => string[];
  /** Per-use carry from revealAfterAction, consumed by the first swing only (never a rider or ranged stray). */
  revealedAttackAdv?: number;
  /** `computed.creature.combat.extra_attack` — the attacker's extra attacks per turn beyond the innate one
   *  (compute/attacks.php; zero-elided). The hindered counter is `weapon_attack − extra_attack`,
   *  derived here from the ROW's live counter: the compiled `hindered_attack_counter` is only fresh after a
   *  recompile, which the client-truth lane no longer does per swing. */
  extraAttack: (row: Row) => number;
  /** The hydrated skill entry's modifier, including per-skill and all-skill status modifiers. */
  skillRollMod: (row: Row, weapon: Row) => number;
  /** The essence bonus folded into the attack modifier (Body for a melee natural weapon). */
  essenceBonus: (row: Row, weapon: Row) => number;
  /** The essence bonus added to the damage roll. */
  damageEssenceBonus: (row: Row, weapon: Row) => number;
  /** Centre-to-centre feet between two rows, for the reach gate. */
  distanceFt: (a: Row, b: Row) => number;
  /** The attacker's reach in feet (`computed.creature.reach.total`). */
  reachFt: (row: Row) => number;
  /** The campaign's crit model: 'maximum' | 'rolled' | 'doubled' (ft_crit_variant_for_campaign).
   *  A campaign house rule — `build_campaign_arr.php:258` folds `campaign_house_rules` into the campaign
   *  array the browser already holds, so all three models are RESOLVED here, never gated. */
  critVariant: (encounter: Row, attacker: Row) => string;
  /** The campaign's exploding-dice house rule (ft_exploding_dice_is_active). */
  explodingDiceActive: (encounter: Row, attacker: Row, target: Row) => boolean;
  /** Campaign crit cascades: Remarkable / World-Shaking extra d20s and the Maimed Fortitude save. */
  critCascadeRules: (encounter: Row, attacker: Row) => CritCascadeRules;
  /** The attack consumes these target riders before a critical hit's save. */
  fortitudeSave?: (target: Row, weapon: Row, consumedStatusIds: readonly string[]) => FortitudeSaveInput | null;
  /** The attacker's Fortune, which widens the explosion threshold (ft_exploding_dice_fortune_of). */
  fortune: (row: Row) => number;
  /** The weapon's damage type id, for the mitigation gate. */
  damageTypeId: (weapon: Row) => string;
  /** `items.item_type` — 'player_character' takes the DYING path, anything else dies outright. */
  itemType: (row: Row) => string;
  /** `computed_value(row, 'vitality', field)` — the COMPUTED vitality cluster, which is what PHP's death
   *  chain reads (`computed_value($target_arr, 'vitality', 'v_max')`), NOT the raw row column. Returns
   *  null when the value is absent, so a missing vitality can fail closed instead of defaulting to 0. */
  vitality: (row: Row, field: 'v_current' | 'v_max') => number | null;
  /** Summons and hunter summons have their own destroy/hide paths at zero vitality. */
  isSummon: (row: Row) => boolean;
  /** Whether it is currently this creature's turn (kill_creature advances the turn if so). */
  isOnTurn: (encounter: Row, row: Row) => boolean;
  /** VISUAL_STATUS_EFFECTS-keyed status presence (movement's `status()` twin), e.g. 'paralyzed'. */
  hasStatus: (row: Row, name: string) => boolean;
  /** Fresh uppercase UUID (operation_id). */
  uuid: () => string;
}

export interface WeaponAttackInput {
  /** Exact inventory binding was prepared and family-checked before this resolver. */
  explicitInventoryWeapon?: boolean;
  throwWeapon?: boolean;
  /** Universal movement reaction; the movement planner owns the provoking reach edge. */
  opportunityAttack?: boolean;
  /** A triggered swing cannot recursively earn another free hand. */
  suppressPartner?: boolean;
  encounterId: string;
  actorId: string;
  targetId: string;
  /** The weapon / natural weapon being swung (a catalog id — the fixture attacker has no inventory). */
  weaponId: string;
  /** The sidebar dice-modifier box. Added to the modifier BEFORE the d20 (skill_roll_inc.php:262). */
  rawDiceMod?: number;
  /** The sidebar advantage/disadvantage box, stacked with every other source. */
  rawDiceAdvDisStacks?: number;
  rollPrivate?: number;
  /** D-012 (owner 2026-09-30, full 3D reach near climbs / ledges): apply_elevation_adv_dis_to_attack_roll's ±1 stack, decided pre-draw by
   *  the caller (directional-cover.ts attackElevationAdv at the swing's positions). Absent = 0. */
  elevationStacks?: number;
  /** Movement-owned flanking verdict; ordinary attacks use attackSpatial instead. */
  flankingSource?: AdvDisSource;
}

export interface AdvDisSource {
  kind: string;
  label: string;
  stacks: number;
}

export interface WeaponAttackOutcome {
  /** Due mitigation-provider gaps from swings that hit; persisted as GM-only skip rows. */
  skippedEffects?: string[];
  /** Reached route work ended the move before this swing; no attack was drawn or paid. */
  swingSkipped?: true;
  /** The target's next-roll riders read by this swing; the shared consequence executor removes their single connections. */
  nextRollAgainstConsumed?: Array<{ targetId: string; statusId: string }>;
  stray?: { targetId: string; intendedTargetId: string; first: WeaponAttackOutcome; firstDeltas: WeaponAttackDeltas };
  skillRollMod?: number;
  weaponAttackMod?: number;
  opportunityAttack?: true;
  /** The face kept from the d20 pair (the "natural"). */
  naturalD20: number;
  /** Every d20 face drawn, in DRAW order; `kept` marks the one that counted. */
  d20Draws: Array<{ value: number; kept: boolean }>;
  advDisStacks: number;
  advDisSources: AdvDisSource[];
  /** The trained-tier die face, or null when the attacker is untrained in the weapon's skill. */
  skillTierDraw: number | null;
  skillTierDie: number | null;
  /** raw + essence + tier — the POST-tier modifier, the same value PHP records in roll_meta.dice.mod. */
  modifier: number;
  /** kept d20 + modifier. */
  result: number;
  dc: number;
  /** Cover raises only the threshold for full damage, never the hit/miss DC. */
  fullAc: number;
  hit: boolean;
  /** True only for the natural-1 auto-miss, so the caller can render the right log row. */
  autoMiss: boolean;
  critical: boolean;
  /** Which campaign crit model resolved the bonus, and what it added. */
  criticalVariant: CritVariant | null;
  criticalBonus: number;
  cascadeTriggers: CritCascadeTrigger[];
  maimedSave: MaimedSave | null;
  /** Automatic Fortunate spent one reaction and one Luck on a swing. */
  fortunateFired: boolean;
  /** The shared swing frame already owns this grant and its next-roll consumption; the S8 tail must not reapply it. */
  interSwingFortunate?: boolean;
  /** The individual dice a Crit: Rolled bonus rolled (empty for Maximum and Doubled, which draw none). */
  criticalTuples: Array<{ value: number; label: string; kind: 'critical' | 'explosion' }>;
  /** Damage dice in index order (empty on a miss). */
  damageDraws: number[];
  damageDice?: Array<{ value: number; kept: boolean }>;
  damageAdvDisStacks?: number;
  /** Explosion links spawned by the BASE damage dice, in chain order. */
  damageExplosions: Array<{ value: number; link: number; threshold: number }>;
  /** Wizard v9 damage_type_is_rolled_at_max: the attacker's provider name when the weapon fragment was rolled at max (else null / absent). */
  damageMaxedBy?: string | null;
  /** The damage row's TOTAL — every fragment after its mitigation, then the untyped adjusters (S10) — what PHP writes to the
   *  damage row's `roll_result` and subtracts from `v_current` (the endpoint checks `v_current == before − damage_row.result`).
   *  Null on a miss. Before S10 this was the weapon fragment alone. */
  damage: number | null;
  /** S10: the extra fragments that rolled after the weapon's (caster passives, weapon-attached, target extra-damage-taken). */
  extraDamage: ExtraDamageFragment[];
  /** S10: calc_total's untyped adjusters that fired, as log tuples in PHP's order (percentages, Damage Mitigation). */
  damageAdjustments: DamageAdjustment[];
  /** S10: the damage row's roll_feedback (rolled / applied, per damage type). Null on a miss. */
  damageFeedback: DamageFeedback | null;
  /** The fragment result BEFORE the mitigation steps (dice + explosions + crit + essence), for the audit trail. */
  damageBeforeMitigation: number | null;
  /** The damage-type mitigation the target's defences applied, in PHP's order (resistance → immunity →
   *  vulnerability, first-wins per layer). Empty when nothing matched the rolled damage type. */
  mitigation: MitigationStep[];
  /** Which reaction fired against this attack, if any. */
  reactionFired: string | null;
  /** The attacker is UNTRAINED in the weapon's skill (compiled tier 0 → `is_flawed`): one disadvantage stack on the d20
   *  and an `untrained` trace entry, skill_roll_inc.php:239. Found missing by the S6 packet (BUG LEDGER 09-08). */
  untrained: boolean;
  /** Evade was present but its `if_caster_has_status_effect_not_active` condition held the target (e.g. Immobile), so
   *  no reaction fired and none was spent — exactly legacy's silent skip in janus() (check_ability_conditions.php:549). */
  evadeBlockedByStatus: string | null;
  /** What the damage did to the target, if anything.
   *   · `dying`          — a player character reduced to 0 (apply_player_dying: prone + dying)
   *   · `killed`         — a non-summon creature reduced to 0 (kill_or_vanish_creature → kill_creature)
   *   · `massive_damage` — a player character hit for >= its MAXIMUM vitality: instant death, no dying
   *                        state at all (apply_damage_to_target_inc.php:435). */
  death: null | 'dying' | 'killed' | 'massive_damage';
  /** Statuses the death branch applies. ⚠ `apply_player_dying` applies **Crippled + Dying + Maimed** (the
   *  first two on one connection id) and REMOVES Prone first; it also drops in-hand inventory and resets
   *  the preferred speed. The earlier "prone + dying" reading was wrong (checked in source, 2026-09-08).
   *  The shared browser composer places this tail in the attack's row plan. */
  statusesApplied: string[];
  /** True when the attacker's `luck` of -1 was SPENT on this roll (caster_is_unlucky): one extra
   *  disadvantage stack, the pool reset to 0 BEFORE the d20, and an `unlucky_roll` log row. */
  luckSpent: boolean;
  /** The attacker's luck AFTER the whole roll, or null when it never moved. Lifecycle, in PHP's order:
   *  caster_is_unlucky spends a -1 BEFORE the d20 (skill_roll_inc.php:1025), then a natural 20 gains
   *  (1162, capped at fortune) or a natural 1 loses (1212, floored at -1). So an already-unlucky
   *  attacker who rolls another 1 goes -1 → 0 → -1. */
  luckAfter: number | null;
  /** S9: the two-weapon RIDER — the free swing of the OTHER hand after the paid action (plan §20), or null when no
   *  partner rides (head slot, or a hand slot with one hand armed). Same AP / counter / roll_id as the paid swing; no
   *  Body on its damage; luck and the target carry across from the paid swing in PHP's order. */
  rider: RiderSwingOutcome | null;
}

/** One rider swing: the paid swing's dice fields for the other hand's weapon. */
export interface RiderSwingOutcome {
  rollType?: string;
  skillRollMod?: number;
  weaponAttackMod?: number;
  maimedSave?: MaimedSave | null;
  weaponId: string;
  /** PHP's toast label: the rider is the hand the paid action did NOT use. */
  label: 'off-hand' | 'main-hand';
  naturalD20: number;
  d20Draws: Array<{ value: number; kept: boolean }>;
  advDisStacks: number;
  advDisSources: AdvDisSource[];
  skillTierDraw: number | null;
  skillTierDie: number | null;
  modifier: number;
  result: number;
  dc: number;
  hit: boolean;
  autoMiss: boolean;
  critical: boolean;
  criticalVariant: CritVariant | null;
  criticalBonus: number;
  cascadeTriggers: CritCascadeTrigger[];
  criticalTuples: Array<{ value: number; label: string; kind: 'critical' | 'explosion' }>;
  damageDraws: number[];
  damageDice?: Array<{ value: number; kept: boolean }>;
  damageAdvDisStacks?: number;
  damageExplosions: Array<{ value: number; link: number; threshold: number }>;
  damageMaxedBy?: string | null;
  damage: number | null;
  damageBeforeMitigation: number | null;
  mitigation: MitigationStep[];
  /** The rider's own extra fragments (its hit's caster / weapon rolls; a target extra-damage-taken the paid hit did not already roll). */
  extraDamage?: ExtraDamageFragment[];
  /** S10: the rider's own row carries the target's Damage Mitigation. */
  damageAdjustments: DamageAdjustment[];
  damageFeedback: DamageFeedback | null;
  untrained: boolean;
  /** The rider spent a −1 the PAID swing left behind (a paid natural 1): its own `unlucky_roll` row + DIS1. */
  luckSpent: boolean;
  dice: number;
  die: number;
  damageTypeId: string;
}

export interface WeaponAttackDeltas {
  /** Shifted hits compare only the final swing's overflow for the creature's massive-damage log. */
  massiveLog?: boolean;
  turnTransition?: TurnDeathTransition;
  attacker: { aps: number; weapon_attack: number; head_attack?: 1; luck?: number; reaction?: 0 };
  /** Only the fields legacy actually writes: vitality on a hit, the reaction when one fired, and on a
   *  death `encounter_creature_dead` + a floored vitality (kill_creature writes exactly those two).
   *  ⚠ Legacy bumps NO revision on the TARGET row (S1 golden §3) — that stays the attacker's. */
  target: { v_current?: number; v_temp?: number; reaction?: 0; encounter_creature_dead?: 1; luck?: number };
  /** Legacy wire field retained for old bundles; death rows are composed by the browser. */
  serverFollowUp: string[];
}

export type WeaponAttackScope =
  | { supported: true; outcome: WeaponAttackOutcome; deltas: WeaponAttackDeltas; draws: Array<{ die: number; value: number }> }
  | { supported: false; refused: true; message: string }
  | { supported: false; refused: false; reason: string };

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => { let v = value; while (Array.isArray(v)) v = v[0]; return v; };
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const int = (value: unknown, fallback = 0): number => {
  const n = Number(scalar(value));
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
};

function itemField(row: Row | null, key: string): unknown {
  const items = record(row?.items);
  return scalar(items?.[key] ?? row?.[key]);
}

/**
 * The adv/dis stacks on this attack, and where each came from.
 *
 *  · the TARGET's Evade, after the caller's trigger settings, conditions and consent gates. In combat it
 *    also needs a reaction; out of combat it spends nothing.
 *  · the ATTACKER's `luck` at exactly -1, which `caster_is_unlucky()` spends as ONE EXTRA disadvantage
 *    stack and then resets to 0 — only while the ENCOUNTER is in combat (`luckActive`; the luck helpers read
 *    the encounter's in_combat). Cross-action state: a natural 1 on a PREVIOUS action changes these dice.
 */
export function resolveAdvDisStacks(
  attackerEc: Row,
  targetEc: Row,
  targetHasEvade: boolean,
  targetInCombat: boolean,
  luckActive: boolean,
  rawStacks: number,
  untrained = false,
  elevation = 0,
  /** The TARGET carries attacks_against_cannot_gain_adv (Foreseen Assault): the net is clamped last, Manual ADV excepted. */
  targetBlocksAdv = false,
  extraSources: AdvDisSource[] = [],
): { stacks: number; sources: AdvDisSource[]; reactionFired: string | null; luckSpent: boolean } {
  const sources: AdvDisSource[] = [];
  let reactionFired: string | null = null;

  // The UNTRAINED (flawed) skill: one disadvantage stack, pushed while the skill roll is BUILT — before the sidebar
  // modifier, the target's reaction and the luck spend (skill_roll_inc.php:239 → 272 → 811 → 1025).
  if (untrained) sources.push({ kind: 'skill_training', label: 'Untrained', stacks: -1 });
  sources.push(...extraSources);
  // D-012: the elevation stack, pushed onto the built skill roll by the attack helpers before the roll runs (damage_roll_attack_helpers.php)
  if (elevation !== 0) sources.push({ kind: 'elevation', label: elevation > 0 ? 'Higher Ground' : 'Lower Ground', stacks: elevation });
  const manual = applyRawDiceModifiers({ rawDiceAdvDisStacks: rawStacks });
  sources.push(...manual.sources);

  // The caller has already applied Evade's db opt-out / target filter, conditions and consent.
  if (targetHasEvade && (!targetInCombat || int(targetEc.reaction) > 0)) {
    sources.push({ kind: 'reaction', label: 'Evade', stacks: -1 });
    reactionFired = EVADE_ID;
  }

  const luckSpent = luckActive && int(attackerEc.luck) === -1;
  if (luckSpent) sources.push({ kind: 'luck', label: 'Unlucky', stacks: -1 });

  if (targetBlocksAdv) {
    const net = sources.reduce((total, s) => total + s.stacks, 0);
    const clamped = clampAttackAdvAgainst(net, manual.manualStacks);
    if (clamped < net) sources.push({ kind: 'passive_ability', label: ATTACKS_AGAINST_CANNOT_GAIN_ADV_LABEL, stacks: clamped - net });
  }

  return { stacks: sources.reduce((total, s) => total + s.stacks, 0), sources, reactionFired, luckSpent };
}

export function resolveWeaponAttack(
  input: WeaponAttackInput,
  encounter: Row,
  attacker: Row,
  target: Row,
  read: WeaponAttackReaders,
  roll: RollSource,
): WeaponAttackScope {
  const weapon = read.definition(upper(input.weaponId));
  const ranged = weapon && weaponAttackRollType(input, weapon, read) === WEAPON_RANGED_ATTACK_ROLL;
  let candidates: Row[] = [];
  let consentPending = false;
  if (ranged) {
    try { candidates = weaponStrayCandidates(encounter, attacker, target); }
    catch (error) { return { supported: false, refused: false, reason: 'weapon:ranged-' + String(error instanceof Error ? error.message : error) }; }
    for (const candidate of candidates.length ? [target, ...candidates] : []) {
      const reason = read.strayTargetPreflight?.(candidate);
      if (reason) return { supported: false, refused: false, reason: 'weapon:ranged-stray-' + reason };
      const candidateId = upper(read.creatureField?.(candidate, 'encounter_creature_id'));
      const rerollLuck = Math.max(-1, int(read.creatureField?.(attacker, 'luck')) - 1);
      const unluckyAttacker = candidate === target ? attacker : { ...attacker, encounter_creatures: { ...record(attacker.encounter_creatures), luck: rerollLuck } };
      try {
        const probe = resolveWeaponAttackSingle({ ...input, targetId: candidateId }, encounter, unluckyAttacker, candidate, read,
          { d: () => { throw STRAY_PREFLIGHT_DRAW; }, fraction: () => { throw STRAY_PREFLIGHT_DRAW; } }, true);
        if (!probe.supported) {
          if (!probe.refused && probe.reason === 'preflight:trigger-consent') consentPending = true;
          else return probe;
        }
      } catch (error) { if (error !== STRAY_PREFLIGHT_DRAW) throw error; }
    }
  }
  if (consentPending) return { supported: false, refused: false, reason: 'preflight:trigger-consent' };
  const first = resolveWeaponAttackSingle(input, encounter, attacker, target, read, roll, false, candidates.length > 0);
  if (!first.supported || first.outcome.naturalD20 !== 1 || !candidates.length) return first;
  const selected = pickStrayCandidate({ roll }, candidates);
  const targetId = upper(read.creatureField?.(selected, 'encounter_creature_id'));
  const rerollAttacker = { ...attacker, encounter_creatures: { ...record(attacker.encounter_creatures), luck: first.outcome.luckAfter ?? read.creatureField?.(attacker, 'luck') ?? 0 } };
  const second = resolveWeaponAttackSingle({ ...input, targetId }, encounter, rerollAttacker, selected, read, roll, true);
  if (!second.supported) throw new Error('Preflighted stray target became unavailable');
  second.outcome.stray = { targetId, intendedTargetId: input.targetId, first: first.outcome, firstDeltas: first.deltas };
  return second;
}

const STRAY_PREFLIGHT_DRAW = new Error('stray-preflight-draw');
export function weaponStrayCandidates(encounter: Row, attacker: Row, target: Row): Row[] {
  const members = savedAttackMembers(encounter);
  return strayCandidates({ encounter: { encounter_creatures_arr: Array.from(members.values()), encounter_inventories_arr: [] }, members }, attacker, target);
}
export function weaponAttackRollType(input: Pick<WeaponAttackInput, 'throwWeapon'>, weapon: Row, read: Pick<WeaponAttackReaders, 'value'>): string {
  if (upper(read.value(weapon, 'weapon_type')) === '1364260D-E37C-4257-AD16-5E745107811A') return WEAPON_MELEE_ATTACK_ROLL;
  return input.throwWeapon ? WEAPON_RANGED_ATTACK_ROLL : upper(read.value(weapon, 'roll_type'));
}

export function resolveWeaponAttackSingle(input: WeaponAttackInput, encounter: Row, attacker: Row, target: Row, read: WeaponAttackReaders,
  roll: RollSource, strayReroll = false, deferNaturalOne = false): WeaponAttackScope {
  const no = (reason: string): WeaponAttackScope => ({ supported: false, refused: false, reason });
  const refuse = (message: string): WeaponAttackScope => ({ supported: false, refused: true, message });

  const attackerEc = record(attacker.encounter_creatures);
  const targetEc = record(target.encounter_creatures);
  if (!attackerEc || upper(attackerEc.encounter_creature_id) !== upper(input.actorId)) return no('actor-row');
  if (!targetEc || upper(targetEc.encounter_creature_id) !== upper(input.targetId)) return no('target-row');
  if (upper(attackerEc.encounter_id) !== upper(input.encounterId)) return no('actor-encounter');
  if (upper(targetEc.encounter_id) !== upper(input.encounterId)) return no('target-encounter');
  if (upper(input.actorId) === upper(input.targetId)) return no('self-target');

  const weapon = read.definition(upper(input.weaponId));
  if (!weapon) return no('weapon-definition-missing');
  const opportunityAttack = input.opportunityAttack === true;
  if (opportunityAttack) {
    if (!read.opposingTeams?.(attacker, target)) return no('opportunity-team-out-of-scope');
    const definition = read.definition(OPPORTUNITY_ATTACK_ID);
    const requiredWeapons = definition ? read.entries(definition, 'if_weapon_type_equipped_active') : [];
    const known = new Set(['cost_gp', 'if_weapon_type_equipped_active', 'requires_in_combat', 'effect_only_affects_opposing_team_type', 'triggers_when_target_leaves_reach', 'triggers_when_target_stands_up_from_prone']);
    if (!definition || Object.keys(record(definition.item_layer_types) ?? {}).some(key => !known.has(key))
      || int(read.value(definition, 'requires_in_combat')) !== 1
      || int(read.value(definition, 'effect_only_affects_opposing_team_type')) !== 1
      || int(read.value(definition, 'triggers_when_target_leaves_reach')) !== 1
      || requiredWeapons.length !== 1
      || upper(typeof requiredWeapons[0] === 'string' ? requiredWeapons[0] : itemField(record(requiredWeapons[0]), 'item_id')) !== '1364260D-E37C-4257-AD16-5E745107811A') return no('opportunity-definition-out-of-scope');
    if (read.entries(attacker, 'triggers_on_opportunity_attack').length || read.entries(target, 'triggers_on_opportunity_attack_against').length) return no('opportunity-rider-out-of-scope');
  }

  // ================= BOUNDARY. Every gate below is a function of STATE, never of a rolled value. =========
  const attackRollType = weaponAttackRollType(input, weapon, read);
  if (attackRollType !== WEAPON_MELEE_ATTACK_ROLL && attackRollType !== WEAPON_RANGED_ATTACK_ROLL) return no('roll-type-out-of-scope');
  if (opportunityAttack && attackRollType !== WEAPON_MELEE_ATTACK_ROLL) return no('opportunity-roll-type-out-of-scope');
  const explicitInventory = input.explicitInventoryWeapon === true;
  if (!explicitInventory && input.throwWeapon) return no('weapon:natural-cannot-be-thrown');
  if (!explicitInventory && !NATURAL_WEAPON.has(catalogKey(read.value(weapon, 'weapon_classification')))) return no('weapon-classification-out-of-scope');
  // Every natural melee slot uses the paid swing economy: Body damage and one counter tick.
  // Only head/hand slots add their slot-specific flag or free-partner handling below.
  const naturalMelee = !explicitInventory && attackRollType === WEAPON_MELEE_ATTACK_ROLL;
  const slotKey = catalogKey(read.value(weapon, 'equipment_slot'));
  const isHeadSlot = naturalMelee && EQUIPMENT_SLOT_HEAD.has(slotKey);
  const isHandSlot = naturalMelee && !isHeadSlot && EQUIPMENT_SLOT_HAND.has(slotKey);

  // Hand-slot selection feeds the shared free-partner executor below.
  // duel_weapon_attack_roll.php swings the OTHER hand once for free after the paid action when BOTH hands are armed:
  // no AP, no counter tick, no Body on its damage. The hand each natural weapon occupies is decided by
  // calculate_equipped_counts() and published in `computed.creature.equipped` (lean: an absent count is 0 — the
  // shared layer reader bridges exactly the keys PHP's layer_value() reads). A PAIRED limb (D2's Slam) carries the
  // SAME id in both hands and the paid swing is the MAIN hand; the rider is then the off hand.
  let riderPlan: WeaponAttackPartner | null = null;
  let paidHandForPartner: 'main' | 'off' | undefined;
  const partnerPlan = (paidHand?: 'main' | 'off'): string | null => {
    paidHandForPartner = paidHand;
    const partner = read.partnerWeapon?.(weapon, attacker, target, paidHand);
    if (partner && 'reason' in partner) return partner.reason;
    riderPlan = partner ?? null;
    return null;
  };
  if (explicitInventory && !opportunityAttack && !input.suppressPartner) {
    const reason = partnerPlan();
    if (reason) return no(reason);
  }
  if (isHandSlot) {
    const weaponKey = upper(input.weaponId);
    const mainNaturalId = upper(readLayerListEntryUuid(read.value(attacker, 'equipped_main_hand_natural_weapon_item_id')));
    const offNaturalId = upper(readLayerListEntryUuid(read.value(attacker, 'equipped_off_hand_natural_weapon_item_id')));
    const countMainWeapon = int(read.value(attacker, 'count_main_hand_weapon_equipped'));
    const countOffWeapon = int(read.value(attacker, 'count_off_hand_weapon_equipped'));
    const countMainNatural = int(read.value(attacker, 'count_main_hand_natural_weapon_equipped'));
    const countOffNatural = int(read.value(attacker, 'count_off_hand_natural_weapon_equipped'));
    if (int(read.value(attacker, 'count_both_hands_weapon_equipped')) > 0) return no('hand-slot-both-hands-weapon-out-of-scope');
    // The d1 fist intentionally has no hand occupancy: it never arms a two-weapon partner.
    // Both ordinary attacks and opportunity attacks can pay one unarmed swing with Body.
    const unarmedFist = weaponKey === ENGINE_FIST_MARKER_ID
      && !mainNaturalId && !offNaturalId && countMainWeapon === 0 && countOffWeapon === 0
      && countMainNatural === 0 && countOffNatural === 0;
    const paidHand: 'main' | 'off' | null = weaponKey !== '' && weaponKey === mainNaturalId ? 'main' : weaponKey !== '' && weaponKey === offNaturalId ? 'off' : unarmedFist ? 'main' : null;
    if (paidHand === null) return no('hand-slot-weapon-not-armed-out-of-scope');
    const mainArmed = countMainWeapon > 0 || countMainNatural > 0;
    const offArmed = countOffWeapon > 0 || countOffNatural > 0;
    if (!opportunityAttack && !input.suppressPartner && read.partnerWeapon) {
      const reason = partnerPlan(paidHand);
      if (reason) return no(reason);
    } else if (mainArmed && offArmed && !opportunityAttack && !input.suppressPartner) {
      const riderHand = paidHand === 'main' ? 'off' : 'main';
      // Arithmetic-only callers without held inventory cannot resolve a manufactured partner.
      if ((riderHand === 'off' ? countOffWeapon : countMainWeapon) > 0) return no('hand-slot-manufactured-partner-out-of-scope');
      const riderId = riderHand === 'off' ? offNaturalId : mainNaturalId;
      // Native parity: an id-less partner contributes no rider. A held id still needs its definition.
      if (/^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(riderId)) {
        const riderDef = read.definition(riderId);
        if (!riderDef) return no('hand-slot-rider-unresolved');
        const riderRollType = weaponAttackRollType({}, riderDef, read);
        if (riderRollType !== WEAPON_MELEE_ATTACK_ROLL && riderRollType !== WEAPON_RANGED_ATTACK_ROLL) return no('rider-roll-type-out-of-scope');
        if (!NATURAL_WEAPON.has(catalogKey(read.value(riderDef, 'weapon_classification')))) return no('rider-weapon-classification-out-of-scope');
        if (read.entries(riderDef, 'uses_ammo_classification').length > 0) return no('hand-slot-rider-ammo-out-of-scope');
        const multiplier = Number(read.value(attacker, 'weapon_ranged_attack_range_multiplier') ?? 0);
        const riderRange = riderRollType === WEAPON_RANGED_ATTACK_ROLL
          ? Number(read.value(riderDef, 'range_end')) * (multiplier > 1 ? multiplier : 1)
          : read.reachFt(attacker) + Math.max(0, Number(read.value(riderDef, 'weapon_reach') ?? 0) - 5);
        if (!Number.isFinite(riderRange) || riderRange <= 0) return no('hand-slot-rider-unresolved');
        if (read.distanceFt(attacker, target) <= riderRange)
          riderPlan = { weaponId: riderId, weapon: riderDef, label: riderHand === 'off' ? 'off-hand' : 'main-hand' };
      }
    }
    // Only one hand armed: PHP returns before any rider — configuration (B), a single swing without a head flag.
  }

  // BOTH creatures' combat membership matters and they are read separately. OUT OF COMBAT the swing still resolves,
  // as legacy's did: an attacker outside combat pays no AP and ticks no counter or head flag (template_weapon_attack_roll's
  // ft_creature_in_combat block); a target outside combat fires Evade with no reaction gate and keeps its reaction
  // (triggered_reaction_ability_can_fire / template_abilities_roll's in-combat spend); and the AP precondition and the
  // luck rules key on the ENCOUNTER's in_combat (check_caster_aps_and_in_combat, luck_and_skill_roll_helpers.php).
  const attackerInCombat = read.inCombat(encounter, attacker);
  const targetInCombat = read.inCombat(encounter, target);
  // An opportunity attack is dispatched only to a combat member (trigger-dispatch inCombatGate); nothing else is one.
  if (opportunityAttack && !attackerInCombat) return no('attacker-out-of-combat');
  const encounterInCombat = attackerInCombat || targetInCombat
    || int(read.encounterField ? read.encounterField(encounter, 'in_combat') : record(encounter.encounters)?.in_combat) === 1;
  const luckActive = encounterInCombat;
  if (int(targetEc.encounter_creature_dead) === 1) return no('target-dead');
  // The whole-action executor must retire concealment and bank its first-attack carry before this arithmetic-only resolver runs.
  // A retained stance (e.g. Quiet Breath) still needs live status modifiers, which this resolver does not model.
  const CONCEALED_STATUS_IDS = ['47667406-1B72-9D60-310B-D8CF6BFE397D', '84B91EEB-2FA6-D1CB-1347-75BFBDA2A6DD'];
  if (read.statusItemIds(attacker).some(id => CONCEALED_STATUS_IDS.includes(upper(id)))) return no('attacker-concealed-out-of-scope');
  // The death branch keys on the target's item type; an unreadable type must never fall through to the creature
  // branch (S5 2026-09-08: a PC row without an `items` bucket read as '' and was KILLED instead of entering Dying).
  const targetType = read.itemType(target);
  if (targetType !== 'player_character' && targetType !== 'creature') return no('target-item-type-unknown');

  // Evade is granted to EVERY encounter creature, so its absence from the rows we were handed means the
  // input is incomplete — not that the reaction cannot fire. Hand back rather than silently resolve an
  // attack with one fewer die than PHP would roll.
  const targetCarriesEvade = read.entries(target, 'reaction_ability')
    .some(entry => upper(typeof entry === 'string' ? entry : itemField(record(entry), 'item_id')) === EVADE_ID);
  if (!targetCarriesEvade) return no('target-evade-missing-incomplete-input');
  // Evade's OWN conditions. janus() runs check_ability_conditions on the reacting creature before anything is logged or
  // spent: `if_caster_has_status_effect_not_active` (Grappled / Immobile / Paralyzed / Restrained) silently skips the
  // reaction — no rows, reaction untouched, the attack rolls without the disadvantage. The definition is read (interned on
  // roster ADD like the natural weapons); a missing definition, an unknown condition layer, or a vitality-status
  // condition hands back pre-draw. (S6 packet 2026-09-08: the Immobile dummy creature never Evades in legacy.)
  const evadeDef = read.definition(EVADE_ID);
  if (!evadeDef) return no('evade-definition-unavailable');
  const evadeLayers = Object.keys(record(evadeDef.item_layer_types) ?? {});
  if (evadeLayers.some(layer => !EVADE_KNOWN_LAYERS.has(layer))) return no('evade-conditions-out-of-scope');
  // The values the resolver assumes: Evade triggers on THIS roll type and imposes its disadvantage on it (skill_roll_inc.php:805
  // pushes -1 per matching status whose roll_type_against_dis lists the rolled type). A definition that no longer says so
  // would change the outcome silently, so it hands back.
  const rollTypeListed = (layer: string): boolean => read.entries(evadeDef, layer).map(entryUuid).includes(attackRollType);
  if (!rollTypeListed('triggers_on_roll_type_against_the_caster') || !rollTypeListed('roll_type_against_dis')) return no('evade-conditions-out-of-scope');
  // Raw compare, as PHP's `===` on the catalog ids (the vitality check stays broad — a hand-back can never resolve wrongly).
  const forbiddenStatusIds = read.entries(evadeDef, 'if_caster_has_status_effect_not_active').map(entry => String(scalar(record(entry)?.item_id ?? record(entry)?.item_layer_effect ?? entry) ?? '')).filter(id => id !== '');
  if (forbiddenStatusIds.some(id => VITALITY_STATUS_IDS.has(upper(id)))) return no('evade-vitality-condition-out-of-scope');
  const targetStatusIds = read.statusItemIds(target);
  const evadeBlockedByStatus = forbiddenStatusIds.find(id => targetStatusIds.includes(id)) ?? null;
  // damage_roll.php suppresses pre-roll Evade while janus_trigger_in_progress(): an
  // Opportunity Attack is already a triggered ability. Preserve the target's reaction.
  let targetHasEvade = !opportunityAttack && !strayReroll && evadeBlockedByStatus === null
    && passesDbGate(read, EVADE_ID, target, String((read.creatureField ?? ecLean)(attacker, 'encounter_creature_id') ?? ''));
  // OWNER RULING 2026-10-02 (lane A11): a player's Evade fires only on its controller's yes — asked only when it would fire (the first swing's
  // gate in resolveAdvDisStacks: out of combat, or in combat with a reaction in hand), decided here before any draw
  if (targetHasEvade && (!targetInCombat || int(targetEc.reaction) > 0) && read.evadeConsent) {
    const answer = read.evadeConsent(target);
    if (answer === undefined) return no('preflight:trigger-consent');
    targetHasEvade = answer;
  }

  // The object-typed defence family (inventory / props; composition roll-up) is its own loop in PHP.
  if (OBJECT_MITIGATION_LAYERS.some(layer => read.entries(target, layer).length > 0)) return no('object-mitigation-out-of-scope');
  // The other damage-result modifiers (rolled at max / min, doubled, halved) sit BEFORE mitigation in damage_type_roll.php
  // and are unported. Direct entries on either creature hand back; status-carried ones are a residual (the weapon's own
  // entries are checked per weapon below).
  for (const layer of DAMAGE_RESULT_MODIFIER_LAYERS) {
    if (read.entries(attacker, layer).length > 0 || read.entries(target, layer).length > 0) return no('damage-result-modifier-out-of-scope');
  }
  // Passive grants can land on the creature root, beyond the reader's own entries.
  const rootLayer = (row: Row, layer: string): boolean => {
    const raw = record(row)?.[layer];
    return Array.isArray(raw) ? raw.length > 0 : (raw !== undefined && raw !== null && raw !== '' && raw !== 0 && raw !== '0');
  };
  const hasLayer = (row: Row, layer: string): boolean => read.entries(row, layer).length > 0 || rootLayer(row, layer);
  // 2026-10-08 follow-up (Perfect Breach BAFC2775 — calc_total_damage_and_apply_to_target.php + check_damage_resistance_and_immunity.php):
  // the attacker's attacks_ignore_damage_mitigation (a weapon hit IS an attack roll) and damage_ignores_all_damage_mitigation (its own row /
  // the root slot finalize_creature_arr folds a passive grant into) skip the target's Damage Mitigation — logged "Damage Mitigation Ignored";
  // its ignore_damage_type_resistance (own list ∪ root) turns a matched resistance of a listed type into "<type> Resistance Ignored". A
  // present-but-falsy flag (PHP !empty() false) is not modelled → hand back.
  const flagOn = (row: Row, layer: string): boolean | null => {
    if (!hasLayer(row, layer)) return false;
    const raw = scalar(read.value(row, layer)) ?? scalar(record(row)?.[layer] as unknown);
    return raw === undefined || raw === null || raw === '' || raw === 0 || raw === '0' || raw === false ? null : true;
  };
  const attackerIgnoresAllDm = flagOn(attacker, DAMAGE_IGNORES_ALL_MITIGATION);
  const attackerAttacksIgnoreDm = flagOn(attacker, 'attacks_ignore_damage_mitigation');
  if (attackerIgnoresAllDm === null || attackerAttacksIgnoreDm === null) return no('mitigation-ignore-out-of-scope');
  const rootIgnoreResistance = record(attacker)?.ignore_damage_type_resistance;
  const ignoreResistanceEntries = [...read.entries(attacker, 'ignore_damage_type_resistance'), ...(Array.isArray(rootIgnoreResistance) ? rootIgnoreResistance : [])];
  if (ignoreResistanceEntries.some(entry => entryUuid(entry) === '')) return no('mitigation-ignore-out-of-scope');
  const ignoreResistance: ReadonlySet<string> = new Set(ignoreResistanceEntries.map(entryUuid));
  /** check_damage_resistance_and_immunity.php's ignore branch: a matched resistance (typed family) of a listed type becomes the ρ tuple,
   *  meta kind 'resistance_ignored' (the source attribution kept), and is not applied. Immunity / vulnerability are never ignored. */
  const markIgnoredResistance = (steps: MitigationStep[], damageTypeId: string, fragment: Row): void => {
    if (!ignoresDamageTypeResistance(damageTypeId, ignoreResistance, fragment, read)) return;
    for (const step of steps) if (step.kind === 'resistance') { step.ignored = true; step.meta = { ...step.meta, kind: 'resistance_ignored' }; }
  };
  if (hasLayer(target, 'crit_to_hit')) return no('target-crit-to-hit-out-of-scope');
  // Painted Shell L40 "A Hundred Styles" (2026-09-16): skill_roll_inc.php drops every DIS source from the attacker's roll. Unported.
  if (hasLayer(attacker, 'ignore_all_attack_roll_dis')) return no('attack-dis-immunity-out-of-scope');
  // Psion Clairvoyant L25 "Foreseen Assault" (2026-10-02): the TARGET's attacks_against_cannot_gain_adv — modelled, clamped in
  // resolveAdvDisStacks (every ADV source this lane folds is in that function; the rest hand back below).
  const targetBlocksAdv = hasLayer(target, ATTACKS_AGAINST_CANNOT_GAIN_ADV);
  // Roll-type ADV/DIS the resolver does not fold in (2026-09-16, Monk v11 PDF review): the ATTACKER's roll_type_adv / _dis and the
  // TARGET's roll_type_against_adv / _dis on this roll type (or the skill_roll alias), carried by an active passive-bucket entry or a
  // status row. PHP pushes each one (skill_roll_inc.php caster/target loops). Evade is not a status here — it is modelled above.
  // An unresolvable passive is skipped HERE because planExtraDamage walks the same buckets and hands it back itself.
  const nextRollAgainstConsumed: NonNullable<WeaponAttackOutcome['nextRollAgainstConsumed']> = [];
  const nextRollAgainstSources: AdvDisSource[] = [];
  {
    const SKILL_ROLL_ALIAS = 'ABF66E1E-DA79-4329-31FA-70D981032C06';
    const lists = (def: Row, layer: string): boolean => read.entries(def, layer).map(entryUuid).some(id => id === attackRollType || id === SKILL_ROLL_ALIAS);
    const defOn = (row: Row, id: string): Row | null => (read.definitionOn ? read.definitionOn(row, id) : read.definition(id));
    const sourcesOf = (row: Row): Array<{ definition: Row | null; status?: Row }> => {
      const out: Array<{ definition: Row | null; status?: Row }> = [];
      for (const raw of read.entries(row, 'passive_ability')) {
        const entry = record(raw);
        if (entry && (entry.passive_conditions_active === false || entry.passive_conditions_active === 0 || entry.passive_conditions_active === '0')) continue;
        const embedded = record(entry?.item_arr);
        if (embedded && record(embedded.item_layer_types)) { out.push({ definition: embedded }); continue; }
        const id = upper(typeof raw === 'string' ? raw : (typeof entry?.item_arr === 'string' ? entry.item_arr : entry?.item_id));
        out.push({ definition: /^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id) ? defOn(row, id) : null });
      }
      const statusList = read.statusRows ? read.statusRows(row) : statusRows(row, 'target');
      for (const status of statusList) {
        const embedded = record(status.status_arr);
        // An unresolvable status stays outside this source walk, matching planExtraDamage's existing residual.
        const id = statusItemId(status);
        const statusDef = (id ? defOn(row, id) : null) ?? (record(embedded?.item_layer_types) ? embedded : null);
        if (statusDef) out.push({ definition: statusDef, status });
      }
      return out;
    };
    for (const { definition: def } of sourcesOf(attacker)) {
      if (!def) continue;
      if ((lists(def, 'roll_type_adv') || lists(def, 'roll_type_dis')) && !read.statusRollSources)
        return no('attacker-roll-type-adv-dis-out-of-scope');
      // Perfect Breach (2026-10-07): damage_ignores_all_damage_mitigation on an active passive / held status of the attacker —
      // calc_total skips Damage Mitigation (ft_damage_ignores_all_damage_mitigation); this lane does not model it. STATE, pre-draw.
      // (Already set on the attacker's own row / root — the folded shape — the provider changes nothing: one ignore, never two.)
      if (!attackerIgnoresAllDm && (read.entries(def, DAMAGE_IGNORES_ALL_MITIGATION).length > 0 || rootLayer(def, DAMAGE_IGNORES_ALL_MITIGATION)))
        return no('mitigation-ignore-out-of-scope');
    }
    for (const { definition: def, status } of sourcesOf(target)) {
      if (!def) continue;
      const rider = status ? nextRollAgainstRider({ entries: read.entries, has: hasLayer }, def, 'target', new Set([attackRollType]),
        { usedRollType: attackRollType, rollerIsTarget: false }) : null;
      if (rider) {
        if (rider.delta) nextRollAgainstSources.push({ kind: 'status_effect', label: String(itemField(def, 'item_name') ?? 'Status Effect'), stacks: rider.delta });
        if (rider.consume) nextRollAgainstConsumed.push({ targetId: upper(input.targetId), statusId: upper(statusScalar(status!, 'status_id')) });
        continue;
      }
      if ((lists(def, 'roll_type_against_adv') || lists(def, 'roll_type_against_dis')) && !(status && read.statusRollSources))
        return no('target-roll-type-against-adv-dis-out-of-scope');
    }
  }
  // Sentinel L25 "Punishing Reach": PHP pushes +1 ADV on an opportunity attack's roll (damage_roll_attack_helpers.php).
  if (opportunityAttack && hasLayer(attacker, 'opportunity_attack_roll_adv')) return no('opportunity-adv-out-of-scope');
  // OA reads global stacks once through the live compiled skill entry, including Poisoned.
  if (!opportunityAttack && (read.entries(attacker, 'skill_rolls_adv').length > 0 || read.entries(attacker, 'skill_rolls_dis').length > 0
    || int(read.value(attacker, 'skill_rolls_adv')) !== 0 || int(read.value(attacker, 'skill_rolls_dis')) !== 0)) {
    return no('global-skill-adv-dis-out-of-scope');
  }

  /** Everything about ONE weapon the swing needs, or the reason it cannot be resolved. Applied to the paid weapon and,
   *  when a rider exists, to the rider weapon — the rider is a plain swing, so it must pass
   *  every weapon-shaped gate the paid swing passes. */
  interface WeaponPlan { weaponId: string; weapon: Row; skillModifiers: boolean; rollType: string; dice: number; die: number; damageTypeId: string; mitigation: MitigationStep[]; tierDie: number | null; untrained: boolean; ignoresDm: boolean; criticalThreshold: number; criticalMultiplier: number;
    /** Wizard v9 damage_type_is_rolled_at_max: the attacker's provider name when this weapon's damage type is rolled at max. */
    maxedBy?: string | null }
  const planWeapon = (planWeaponId: string, w: Row, inventory = explicitInventory): WeaponPlan | { reason: string } => {
    // the weapon's own attacks_ignore_damage_mitigation / damage_ignores_all_damage_mitigation ($item_id_used_arr in calc_total)
    const weaponAttacksIgnoreDm = flagOn(w, 'attacks_ignore_damage_mitigation'), weaponIgnoresAllDm = flagOn(w, DAMAGE_IGNORES_ALL_MITIGATION);
    if (weaponAttacksIgnoreDm === null || weaponIgnoresAllDm === null) return { reason: 'mitigation-ignore-out-of-scope' };
    // --- damage-type mitigation (S6): RESOLVED, in PHP's order, first-wins per layer, on the rolled damage type. ---
    // check_damage_resistance_and_immunity.php runs resistance → immunity → vulnerability over the target's defence
    // entries (the compile's computed.creature.defenses store, bridged by both readers), then the object-typed family,
    // then the spell-keyed vulnerability — which never touches a weapon fragment, so it needs no gate here. Everything
    // matched is STATE, so it is settled before the draws; the arithmetic is applied to the fragment result below.
    // The RAW stored id (no case normalisation): PHP's `$damage_roll_arr[i]['damage_type']` is the layer value as stored and
    // every compare / meta below uses it verbatim. A malformed id cannot be matched faithfully, so it hands back.
    const damageTypeId = String(scalar(read.damageTypeId(w)) ?? '');
    if (!/^[0-9A-Fa-f]{8}(-[0-9A-Fa-f]{4}){3}-[0-9A-Fa-f]{12}$/.test(damageTypeId)) return { reason: 'weapon-damage-type-unreadable' };
    const mitigation: MitigationStep[] = [];
    for (const { layer, kind } of MITIGATION_LAYERS) {
      const entry = matchDefenseEntry(read.entries(target, layer), damageTypeId);
      if (entry) mitigation.push({ kind, layer, meta: defenseMeta(kind, layer, damageTypeId, entry) });
    }
    // Only this fragment's own flag applies; a nested maneuver does not alter the weapon/off-hand fragment.
    markIgnoredResistance(mitigation, damageTypeId, w);
    if (mitigation.some(step => step.kind === 'immunity') && read.entries(w, 'effect_ignores_damage_type_immunity').length > 0) {
      return { reason: 'mitigation-ignore-out-of-scope' };
    }
    for (const layer of DAMAGE_RESULT_MODIFIER_LAYERS) if (read.entries(w, layer).length > 0) return { reason: 'damage-result-modifier-out-of-scope' };
    // Explicit bindings materialize the selected weapon's enchantment modifier.
    // Natural swings retain their existing scope; unreadable tags never become 0.
    const weaponAttackMod = read.value(w, 'weapon_attack_mod');
    if (weaponAttackMod != null && (!inventory || !Number.isInteger(Number(weaponAttackMod))))
      return { reason: 'weapon-attack-modifier-out-of-scope' };
    const skillModifiers = inventory || explicitInventory || opportunityAttack;
    if (!skillModifiers && int(read.skillRollMod(attacker, w)) !== 0) return { reason: 'skill-roll-modifier-out-of-scope' };
    // The page reader resolves lean tier-0 omissions; a missing skills cluster
    // remains unknown. Inventory swings also include the compiled skill stacks.
    const skillTierLevel = read.skillTierLevel(attacker, w);
    if (skillTierLevel === null || !Number.isFinite(skillTierLevel)) return { reason: 'skill-entry-missing' };
    if (skillTierLevel < 0) return { reason: 'skill-tier-unreadable' };
    if (!skillModifiers && int(read.skillAdvDisStacks(attacker, w)) !== 0) return { reason: 'skill-adv-dis-out-of-scope' };
    const dice = int(read.value(w, 'dice'));
    const die = int(read.value(w, 'die'));
    if (dice <= 0 || die <= 0) return { reason: 'weapon-damage-dice-missing' };
    // Knockout Strike — a non_lethal_attack ability knocks out instead of killing (apply_damage_to_target_inc.php).
    if (read.entries(w, 'non_lethal_attack').length > 0) return { reason: 'non-lethal-knockout-out-of-scope' };
    // F5: PHP's damage fragment carries a roll MODIFIER alongside the dice and the essence bonus. The
    // fixture's natural weapon has no modifier layer (its mod equals the essence exactly, per the S1
    // golden), and the general modifier assembly is not ported — so a weapon that carries one hands back.
    if (read.entries(w, 'roll_mod').length > 0 || read.entries(w, 'damage_mod').length > 0) return { reason: 'weapon-damage-modifier-out-of-scope' };
    const tierDie = read.skillTierDie(attacker, w);
    return { weaponId: upper(planWeaponId), weapon: w, skillModifiers,
      rollType: planWeaponId === input.weaponId ? attackRollType : weaponAttackRollType({}, w, read), dice, die, damageTypeId, mitigation, tierDie: tierDie ?? null, untrained: skillTierLevel === 0,
      criticalThreshold: attackCriticalHitThreshold({ read }, attacker, w),
      criticalMultiplier: criticalHitDamageMultiplier(read, attacker, w),
      ignoresDm: !!(attackerAttacksIgnoreDm || attackerIgnoresAllDm || weaponAttacksIgnoreDm || weaponIgnoresAllDm) }; // compute/_base.php:622 `'is_flawed' => $tl === 0`
  };
  const paidPlanOrNo = planWeapon(input.weaponId, weapon);
  if ('reason' in paidPlanOrNo) return no(paidPlanOrNo.reason);
  const paid: WeaponPlan = paidPlanOrNo;
  let rider: (WeaponPlan & { label: 'off-hand' | 'main-hand' }) | null = null;
  if (riderPlan) {
    if (shieldedChanges(target).length) return no('shielded-rider-out-of-scope');
    const riderPlanOrNo = planWeapon(riderPlan.weaponId, riderPlan.weapon, riderPlan.explicitInventoryWeapon === true);
    if ('reason' in riderPlanOrNo) return no('rider-' + riderPlanOrNo.reason);
    rider = { ...riderPlanOrNo, label: riderPlan.label };
    // A second projectile target needs its own settlement/log slot; never silently drop a due stray shot.
    if (rider.rollType === WEAPON_RANGED_ATTACK_ROLL) {
      try {
        if (weaponStrayCandidates(encounter, attacker, target).length) return no('rider-ranged-stray-out-of-scope');
      } catch (error) { return no('rider-ranged-' + String(error instanceof Error ? error.message : error)); }
    }
  }

  // --- S10: the rest of the damage pipeline — extra fragments + calc_total's untyped adjusters (weapon-attack-extra-damage.ts). ---
  // Settled on STATE before any draw; whatever is not ported hands the whole attack back.
  const extraRead: ExtraDamageReaders = {
    value: read.value, entries: read.entries, statusItemIds: read.statusItemIds, hasStatus: read.hasStatus,
    definitionOn: read.definitionOn ?? ((_row, id) => read.definition(id)),
    statusRows: read.statusRows ?? (row => { const list = record(row.statuses)?.target; return Array.isArray(list) ? list.map(record).filter((s): s is Row => s !== null) : []; }),
    member: id => Object.values((encounter.encounter_creatures_arr ?? {}) as object).map(record)
      .find(row => upper(record(row?.encounter_creatures)?.encounter_creature_id) === id) ?? undefined,
    conditionScene: () => read.conditionScene?.(attacker) ?? { encounter, campaign: undefined },
  };
  /** The same first-wins defence match + ignore gates the weapon fragment uses, for an extra fragment's damage type. */
  const mitigationFor = (w: Row) => (damageTypeId: string, fragment: Row): MitigationStep[] | { reason: string } => {
    const steps: MitigationStep[] = [];
    for (const { layer, kind } of MITIGATION_LAYERS) {
      const entry = matchDefenseEntry(read.entries(target, layer), damageTypeId);
      if (entry) steps.push({ kind, layer, meta: defenseMeta(kind, layer, damageTypeId, entry) });
    }
    markIgnoredResistance(steps, damageTypeId, fragment);
    if (steps.some(step => step.kind === 'immunity') && read.entries(w, 'effect_ignores_damage_type_immunity').length > 0) return { reason: 'mitigation-ignore-out-of-scope' };
    return steps;
  };
  const pipelineOf = (w: Row) => planExtraDamage({
    attacker, target, weapon: w, attackerId: String(attackerEc.encounter_creature_id ?? ''), meleeRollType: weaponAttackRollType({}, w, read),
    read: { ...extraRead, entries: (row, key) => read.weaponEntries?.(w, row, key) ?? extraRead.entries(row, key) },
    mitigationFor: mitigationFor(w), vitalityStatusIds: VITALITY_STATUS_IDS,
  });
  const initialPipeline = pipelineOf(paid.weapon);
  if (!initialPipeline.supported) return no(explicitInventory && initialPipeline.sourceName && !initialPipeline.reason.endsWith('status-passive-out-of-scope')
    ? 'weapon:family:enchantment.abilities:' + initialPipeline.sourceName + ':' + initialPipeline.reason
    : initialPipeline.reason + (initialPipeline.sourceName && initialPipeline.reason.endsWith('status-passive-out-of-scope') ? ':' + initialPipeline.sourceName : ''));
  let pipeline: Extract<typeof initialPipeline, { supported: true }> = initialPipeline;
  // Every die becomes a log tuple the endpoint bounds (256 per row, plus crit dice and explosion links). A fan-out that could exceed it
  // would be refused AFTER the draws — hand it back before instead (Codex S10 review F4).
  if (pipeline.plans.length > 0 && paid.dice + pipeline.plans.reduce((sum, p) => sum + p.dice, 0) > 64) return no('damage-dice-budget-out-of-scope');
  // The rider plans its OWN pipeline (its weapon's attached fragments and providers). PHP runs damage_type_rolls once per strike: the
  // attacker's extra fragments roll on each hit, the target's extra-damage-taken once per ACTION (owner ruling 2026-07-22,
  // `$edt_rolled_per_action`) — the rider drops each grant the paid hit already rolled (riderPlans below). Owner playtest 2026-10-21:
  // Sorcerer Guy's paired Slams under a status-held "Extra Damage - Cold (10d4)" refused here and fell back to the janus lane.
  let riderPipeline: typeof pipeline | null = null;
  if (rider) {
    const planned = pipelineOf(rider.weapon);
    if (!planned.supported) return no('rider-' + planned.reason);
    if (planned.plans.length > 0 && rider.dice + planned.plans.reduce((sum, p) => sum + p.dice, 0) > 64) return no('damage-dice-budget-out-of-scope');
    riderPipeline = planned;
  }
  // Wizard v9 damage_type_is_rolled_at_max (ability/caster-spell-layers.ts — the spell lane's same walk): the ATTACKER's maxed damage types
  // (root / active passive / feat grant / ACTIVE status — Avatar of Evocation rides a combat-start self-status), settled on state before the
  // draws (after the extra-damage plan, so its own hand-back reasons keep precedence). A weapon or extra fragment of a listed type
  // reads dice × die, never explodes, and logs damage_type_roll's Ω tuple.
  const maxTypes = damageTypesRolledAtMax({
    value: (row: Row | null, key: string) => row ? read.value(row, key) : null,
    has: (row: Row | null, key: string) => !!row && (read.value(row, key) != null || read.entries(row, key).length > 0),
    entries: (row: Row | null, key: string) => row ? read.entries(row, key) : [],
    // an unresolvable STATUS is skipped, not handed back — this lane's recorded residual (the ADV / DIS walk above, planExtraDamage)
    statuses: (row: Row) => (read.statusRows ? read.statusRows(row)
      : (Array.isArray(record(row.statuses)?.target) ? (record(row.statuses)?.target as unknown[]).map(record).filter((st): st is Row => st !== null) : []))
      .filter((st) => ['item_layer_types', 'layers', 'computed'].some((key) => record(record(st.status_arr)?.[key]) !== null)
        || (() => { const id = upper(scalar(st.status_item_id) ?? itemField(record(st.status_arr), 'item_id')); return !!id && !!(read.definitionOn ? read.definitionOn(row, id) : read.definition(id)); })()),
    inCombat: () => false,
    resolveItem: (entry: unknown) => {
      const id = entryUuid(entry) || upper(itemField(record(entry), 'item_id'))
        || upper(itemField(record(record(entry)?.item_arr), 'item_id'));
      return id ? (read.definitionOn ? read.definitionOn(attacker, id) : read.definition(id)) : null;
    },
  } as AbilityPreconditionReaders, attacker);
  if (maxTypes === undefined) return no('damage-type-rolled-at-max-provider-unproved');
  paid.maxedBy = maxTypes.get(upper(paid.damageTypeId)) ?? null;
  if (rider) rider.maxedBy = maxTypes.get(upper(rider.damageTypeId)) ?? null;

  // Each rule has its own browser draws; a Maimed save must be completely readable before attacking.
  const cascadeRules = read.critCascadeRules(encounter, attacker);
  const saveInput = cascadeRules.maimed ? read.fortitudeSave?.(target, paid.weapon, nextRollAgainstConsumed.map(rider => rider.statusId)) : null;
  const riderSaveInput = cascadeRules.maimed && rider
    ? read.fortitudeSave?.(target, rider.weapon, nextRollAgainstConsumed.map(status => status.statusId)) : null;
  if (cascadeRules.maimed && !saveInput) return no('maimed-save-input-unavailable');
  if (cascadeRules.maimed && rider && !riderSaveInput) return no('maimed-save-input-unavailable');
  const maimedEvadeEnabled = passesDbGate(read, EVADE_ID, target,
    String((read.creatureField ?? ecLean)(attacker, 'encounter_creature_id') ?? ''));

  // --- eligibility, in the order use_ability_pre_conditions checks it ---
  if (statusPreventsActions(attacker, read, opportunityAttack ? 'no_reactions' : 'no_actions')) return refuse(opportunityAttack
    ? 'You cannot use reactions while an active status prevents reactions.'
    : 'You cannot take actions while an active status prevents actions.');
  const hasMultipleHeadAttacks = read.entries(attacker, 'multiple_head_attacks').length > 0
    || Boolean(read.value(attacker, 'multiple_head_attacks'));
  if (attackerInCombat && isHeadSlot && !hasMultipleHeadAttacks && int(attackerEc.head_attack) === 1) {
    // Legacy text, verbatim (use_ability_pre_conditions.php:751).
    return refuse('You can only make an attack with your head once per turn!');
  }

  // Reach FAILS CLOSED: an unmeasurable distance is not an eligible attack, it is an unknown one.
  const weaponReach = catalogKey(read.value(weapon, 'weapon_type')) === '1364260D-E37C-4257-AD16-5E745107811A'
    ? Number(read.value(weapon, 'weapon_reach') ?? 0) : 0;
  if (!Number.isFinite(weaponReach) || weaponReach < 0) return no('weapon:family:weapon_reach');
  const reach = read.reachFt(attacker) + Math.max(0, weaponReach - 5);
  const distance = read.distanceFt(attacker, target);
  if (!Number.isFinite(reach) || !Number.isFinite(distance)) return no('reach-unmeasurable');
  const rangedSources = (w: Row, rollType: string, throwing = false): AdvDisSource[] | { reason: string } => {
    const sources: AdvDisSource[] = [];
    const prefix = throwing ? 'weapon:throw-' : 'weapon:ranged-';
    const authoredMultiplier = Number(read.value(attacker, 'weapon_ranged_attack_range_multiplier') ?? 0);
    const multiplier = !throwing && authoredMultiplier > 1 ? authoredMultiplier : 1;
    const normal = Number(read.value(w, 'range_begin')) * multiplier, maximum = Number(read.value(w, 'range_end')) * multiplier;
    if (!Number.isFinite(normal) || !Number.isFinite(maximum) || normal <= 0 || maximum < normal) return { reason: prefix + 'range-unavailable' };
    if (!strayReroll && distance > maximum) return { reason: prefix + 'out-of-range' };
    if (!read.encounterField) return { reason: prefix + 'environment-reader-unavailable' };
    if (upper(read.encounterField(encounter, 'encounter_environment') ?? read.value(encounter, 'location_environment')) === '19BCE18F-E1DC-D359-48A0-3B789741E058')
      return { reason: prefix + 'underwater' };
    if (read.entries(attacker, 'passive_ability').some(entry => entryUuid(entry) === 'D11FB4D0-3A08-A17D-5151-9AEAA1F78660'))
      return { reason: prefix + 'dead-eye-activity' };
    if (!read.opposingTeams) return { reason: prefix + 'team-unavailable' };
    const ability = record(w.attack_ability_arr);
    if (rollType === WEAPON_RANGED_ATTACK_ROLL && distance > normal
      && ![attacker, w, ability].some(row => row && read.value(row, 'ignore_long_range_attack_roll_dis')))
      sources.push({ kind: 'weapon_range', label: 'Long Range', stacks: -1 });
    if (rollType === WEAPON_RANGED_ATTACK_ROLL && distance <= read.reachFt(attacker) && read.opposingTeams(attacker, target)
      && !read.value(attacker, 'ignore_ranged_weapon_attack_roll_dis_within_an_enemy_reach')) sources.push({ kind: 'weapon_range', label: 'Ranged in Melee', stacks: -1 });
    return sources;
  };
  const rangeSources = attackRollType === WEAPON_RANGED_ATTACK_ROLL || input.throwWeapon ? rangedSources(weapon, attackRollType, input.throwWeapon) : [];
  if (!Array.isArray(rangeSources)) return no(rangeSources.reason);
  if (attackRollType === WEAPON_MELEE_ATTACK_ROLL && !input.throwWeapon && distance > reach) return no('out-of-reach');
  const riderRangeSources = rider?.rollType === WEAPON_RANGED_ATTACK_ROLL ? rangedSources(rider.weapon, rider.rollType) : [];
  if (!Array.isArray(riderRangeSources)) return no(riderRangeSources.reason);

  const aps = int(attackerEc.aps);
  // Out of combat waives resource costs, not status restrictions (check_caster_aps_and_in_combat).
  if (encounterInCombat && opportunityAttack && int(attackerEc.reaction) <= 0) return refuse('You have already used your reaction this round.');
  if (encounterInCombat && !opportunityAttack && aps < ATTACK_AP_COST) return refuse('You do not have enough action points to use this ability!');
  // Both hands share this action's starting budget; the paid attack increments it once.
  const hindered = hinderedAttackStacksFromCounter(int((read.creatureField ?? ecLean)(attacker, 'weapon_attack')) - int(read.extraAttack(attacker)));

  let spatial: WeaponAttackSpatialContext | undefined;
  let riderSpatial: WeaponAttackSpatialContext | undefined;
  if (!opportunityAttack && read.attackSpatial) {
    try { spatial = read.attackSpatial(attacker, target, weapon, { melee: attackRollType === WEAPON_MELEE_ATTACK_ROLL, throwing: input.throwWeapon === true }); }
    catch (error) {
      if (error instanceof Refusal) return refuse(error.refusal);
      if (error instanceof HandBack) return no(error.reason);
      throw error;
    }
  }
  if (rider && read.attackSpatial) {
    try { riderSpatial = read.attackSpatial(attacker, target, rider.weapon, { melee: rider.rollType === WEAPON_MELEE_ATTACK_ROLL, throwing: false }); }
    catch (error) {
      if (error instanceof Refusal) return refuse(error.refusal);
      if (error instanceof HandBack) return no(error.reason);
      throw error;
    }
  }
  const initialDc = read.armorClass(target);
  if (initialDc === null || !Number.isFinite(initialDc)) return no('target-ac-unavailable');
  let dc: number = initialDc;
  const fullAc = Math.max(dc, initialDc + (spatial?.acDelta ?? 0));
  let damageReduction = 0;
  if (fullAc > dc || (riderSpatial?.acDelta ?? 0) > 0) {
    try { damageReduction = targetDamageReduction({ read }, target); }
    catch (error) {
      if (error instanceof HandBack) return no(error.reason);
      throw error;
    }
  }
  const damageEssence = int(read.damageEssenceBonus(attacker, paid.weapon));

  // The death decision is driven by vitality, so an ABSENT vitality must never be read as zero. `int()`
  // would silently turn a missing v_current into 0 and make an ordinary hit lethal; a missing v_max
  // would disable the massive-damage branch and downgrade a death into a dying state. Both fail closed,
  // pre-draw (Codex review 2026-09-07).
  const vBefore = read.vitality(target, 'v_current');
  const vMax = read.vitality(target, 'v_max');
  if (vBefore === null || !Number.isFinite(vBefore)) return no('target-vitality-unavailable');
  if (vMax === null || !Number.isFinite(vMax) || vMax <= 0) return no('target-max-vitality-unavailable');
  const shifted = int(read.value(target, 'shapeshifted')) === 1;
  if (shifted && !read.absorbFormDamage) return no('lost_shapeshift:lane-cannot-persist');

  // Campaign facts, resolved before the draws so the draw block is pure arithmetic.
  const variant = (String(read.critVariant(encounter, attacker) ?? 'maximum') as CritVariant);
  const canExplode = Boolean(read.explodingDiceActive(encounter, attacker, target));
  const formTemp = shifted ? temporaryVitality(target) : 0;
  const maximumDamage = (plan: WeaponPlan, bonus: number, through: typeof pipeline = pipeline): number => {
    const base = plan.dice * plan.die + bonus;
    const critical = (plan.criticalMultiplier - 1) * (variant === 'doubled' ? base : variant === 'rolled'
      ? Math.ceil(critRolledDiceTarget(plan.dice, plan.die)) * plan.die : plan.dice * plan.die);
    const cascades = Number(cascadeRules.remarkable) + Number(cascadeRules.worldShaking);
    const beforeMitigation = canExplode && !plan.maxedBy ? Infinity : base + critical + cascades * plan.dice * plan.die;
    const fragment = mitigateFragment(beforeMitigation, plan.mitigation);
    if (through.outputPercent === -100 || through.incomingPercent === -100) return 0;
    if (!Number.isFinite(fragment.result)) return fragment.result;
    return totalDamage([{ damageTypeId: plan.damageTypeId, beforeMitigation, ...fragment }],
      { ...through, mitigationIgnored: plan.ignoresDm }, { d: () => 1, fraction: () => 0 }).total;
  };
  // Both hands can share the form while it survives. A possible revert needs a mid-swing recompile and a rider log slot.
  // The bound models no extra fragment, so any fragment on either hand counts as a possible revert.
  if (shifted && rider && formTemp > 0 && (pipeline.plans.length > 0 || (riderPipeline?.plans.length ?? 0) > 0
    || maximumDamage(paid, damageEssence) + maximumDamage(rider, 0, riderPipeline ?? pipeline) >= formTemp))
    return no('lost_shapeshift:lane-cannot-persist');
  const essence = int(read.essenceBonus(attacker, paid.weapon));
  const attackerFortune = int(read.fortune(attacker));
  // janus_trigger spends the OA reaction before the weapon roll; gaining luck cannot spend it again.
  const fortunateOnGain = luckActive && !opportunityAttack && (read.fortunateOnGain?.(attacker) ?? false);
  // S9: the RIDER rolls from the pool the paid swing leaves. A paid natural 1 lowers it by one, so the rider can gain where
  // the paid swing could not (the luck cap) — evaluate the same universal-Fortunate gate against THAT pool, so a competing
  // unsupported carrier is refused before any draw, just as on the paid swing.
  const luckAfterPaidSpend = int(attackerEc.luck) === -1 ? 0 : int(attackerEc.luck);
  const fortunateOnGainRider = riderPlan && luckActive
    ? (read.fortunateOnGain?.({ ...attacker, encounter_creatures: { ...attackerEc, luck: Math.max(-1, luckAfterPaidSpend - 1) } }) ?? false)
    : false;
  const isPlayerCharacter = targetType === 'player_character';

  // The off-hand feat gives the rider's damage Body. This lane reads direct entries; provider-only grants remain a residual (§20.2).
  if (rider && (read.entries(attacker, 'off_hand_body_damage').length > 0 || Boolean(read.value(attacker, 'off_hand_body_damage'))))
    return no('hand-slot-off-hand-body-feat-out-of-scope');

  const reactionGap = read.damageReactionPreflight?.(attacker, target,
    targetHasEvade && targetInCombat && int(targetEc.reaction) > 0,
    [...new Set([paid.damageTypeId, ...pipeline.plans.map(plan => plan.damageTypeId), ...(rider ? [rider.damageTypeId] : []),
      ...(riderPipeline?.plans.map(plan => plan.damageTypeId) ?? [])])],
    shifted ? { reverts: formTemp > 0 && (pipeline.plans.length > 0 || maximumDamage(paid, damageEssence) >= formTemp),
      vitalityDamage: pipeline.plans.length > 0 || maximumDamage(paid, damageEssence) > formTemp } : undefined);
  if (reactionGap) return no(reactionGap);

  // ================= DRAWS. Death settlement uses the actual outcome below. =============================
  // One luck lifecycle and one target carry across BOTH swings, in PHP's order (plan §20.1 / §20.5).
  const luckBefore = int(attackerEc.luck);
  const state = { luckNow: luckBefore, luckMoved: false, targetReaction: int(targetEc.reaction), vNow: vBefore,
    fortunateFired: false, interSwingFortunate: false };
  const skippedEffects = new Set<string>();
  let lastAppliedDamage: number | null = null;
  interface SwingResult {
    d20: ReturnType<typeof rollWithStacks>; stacks: number; sources: AdvDisSource[]; reactionFired: string | null; luckSpent: boolean;
    tierDraw: number | null; modifier: number; result: number; hit: boolean; autoMiss: boolean; critical: boolean;
    criticalVariant: CritVariant | null; criticalBonus: number; criticalTuples: WeaponAttackOutcome['criticalTuples']; cascadeTriggers: CritCascadeTrigger[];
    damageDraws: number[]; damageDice: Array<{ value: number; kept: boolean }>; damageAdvDisStacks: number; damageExplosions: Array<{ value: number; link: number; threshold: number }>; damage: number | null; damageBeforeMitigation: number | null;
    extraDamage: ExtraDamageFragment[]; casterHealing: number; damageAdjustments: DamageAdjustment[]; damageFeedback: DamageFeedback | null;
  }
  /** `allowFortunate` = the universal-Fortunate gate evaluated for THIS swing's starting pool (paid: the row; rider: the pool a paid natural 1 would leave). */
  const swing = (plan: WeaponPlan, swingEssence: number, swingDamageEssence: number, allowFortunate: boolean, extraPlans: readonly ExtraDamagePlan[], revealedAdv = 0,
    nextRollSources: AdvDisSource[] = [], dying = false): SwingResult => {
    const swingSpatial = plan === paid ? spatial : riderSpatial;
    const swingFullAc = plan === paid ? fullAc : Math.max(dc, dc + (riderSpatial?.acDelta ?? 0));
    const skillStacks = plan.skillModifiers ? int(read.skillAdvDisStacks(attacker, plan.weapon)) : 0;
    const skillSources = plan.skillModifiers && opportunityAttack && read.skillAdvDisSources
      ? read.skillAdvDisSources(attacker, plan.weapon) : skillStacks ? [{ kind: 'skill_base', label: 'Skill', stacks: skillStacks }] : [];
    // `resolveAdvDisStacks` counts the unlucky spend into `stacks` from the pool AS IT STANDS at this swing (a paid
    // natural 1 leaves −1 for the rider to spend) and the target's reaction as the PAID swing left it.
    const { stacks, sources, reactionFired, luckSpent } = resolveAdvDisStacks(
      { ...attackerEc, luck: state.luckNow }, { ...targetEc, reaction: state.targetReaction }, targetHasEvade, targetInCombat, luckActive, int(input.rawDiceAdvDisStacks), plan.untrained,
      swingSpatial ? 0 : Math.sign(int(input.elevationStacks)), targetBlocksAdv, [...(plan === paid ? rangeSources : riderRangeSources), ...(swingSpatial?.sources ?? []), ...nextRollSources,
        ...(!swingSpatial && opportunityAttack && input.flankingSource ? [input.flankingSource] : []),
        ...(hindered ? [{ kind: 'hindered_attack', label: 'Hindered Attack', stacks: -hindered }] : []),
        ...(read.swingRollSources?.(target, plan.weaponId, plan.rollType, plan !== paid) ?? []),
        ...(!read.statusRollSources && plan !== paid && state.fortunateFired
          ? [{ kind: 'status_effect', label: 'Fortunate', stacks: 1 }] : []),
        ...(revealedAdv ? [{ kind: 'status_effect', label: 'Revealed Attack', stacks: revealedAdv }] : []),
        ...skillSources],
    );
    if (reactionFired && targetInCombat) state.targetReaction = 0;
    // 1. the attack d20. caster_is_unlucky resets the pool it spends (before the roll).
    if (luckSpent) { state.luckNow = 0; state.luckMoved = true; }
    const reactionAttacker = { ...attacker, encounter_creatures: { ...attackerEc, luck: state.luckNow,
      aps: plan === paid && attackerInCombat ? aps - (opportunityAttack ? 0 : ATTACK_AP_COST) : int(attackerEc.aps),
      ...(state.fortunateFired ? { reaction: 0 } : {}) } };
    const reactionTarget = { ...target, encounter_creatures: { ...targetEc, reaction: state.targetReaction } };
    read.postRollPreDraw?.(reactionAttacker, reactionTarget, plan.rollType);
    const d20 = rollWithStacks(roll, 20, stacks);
    // A natural 20 gains (capped at fortune); a natural 1 loses (floored at -1). Both only while the encounter is in
    // combat, and both AFTER the roll — so an attacker who spent a -1 above and then rolls a 1 lands back on -1.
    if (luckActive && d20.kept === 20) {
      const gained = Math.min(attackerFortune, state.luckNow + 1);
      if (gained !== state.luckNow) {
        const increased = gained > state.luckNow;
        state.luckNow = gained; state.luckMoved = true;
        if (allowFortunate && !state.fortunateFired && increased && int(attackerEc.reaction) > 0 && state.luckNow >= 1) {
          if (plan === paid && rider && read.applyInterSwingFortunate) {
            read.applyInterSwingFortunate(state.luckNow);
            state.interSwingFortunate = true;
          }
          state.luckNow -= 1;
          state.fortunateFired = true;
        }
      }
    } else if (luckActive && d20.kept === 1) {
      const lost = Math.max(-1, state.luckNow - 1);
      if (lost !== state.luckNow) { state.luckNow = lost; state.luckMoved = true; }
    }
    // 2. the trained-tier die — AFTER the d20, and only when the attacker is trained.
    const tierDraw = plan.tierDie && plan.tierDie > 0 ? roll.d(plan.tierDie) : null;
    const modifier = applyRawDiceModifiers(input, swingEssence + (tierDraw ?? 0)
      + (plan.skillModifiers ? int(read.skillRollMod(attacker, plan.weapon)) + int(read.value(plan.weapon, 'weapon_attack_mod')) : 0)).modifier;
    const result = d20.kept + modifier;
    // A natural 1 ALWAYS misses; there is no natural-20 auto-hit (get_damage_application_info §2).
    const autoMiss = d20.kept === 1;
    const hit = !autoMiss && result >= dc;
    const criticalEnv = browserDependencies({
      has_status_effect: (_row: unknown, id: string): boolean => {
        const name = catalogVariable('StatusEffect', id, criticalEnv);
        return name !== null && (name === 'dying' && dying || read.hasStatus(target, name));
      },
      layer_has: (row: Row, key: string) => read.entries(row, key).length > 0,
      layer_value: read.value,
    });
    const critical = hit && (d20.kept >= plan.criticalThreshold
      || targetIsAutoCriticalForAttackRoll(target, plan.rollType, criticalEnv));
    // 3. the damage, only on a hit. The order below is PHP's, verified against damage_type_roll.php:
    //    base dice (line 487) → the explosion pass over them (line 535) → the crit hook (line 822).
    const damageDraws: number[] = [];
    const damageDice: Array<{ value: number; kept: boolean }> = [];
    const damageAdvDisStacks = rollTypeItemStacks(plan.weapon, '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0', read.entries);
    const damageExplosions: Array<{ value: number; link: number; threshold: number }> = [];
    let damage: number | null = null;
    let damageBeforeMitigation: number | null = null;
    let criticalBonus = 0;
    let criticalVariant: CritVariant | null = null;
    let criticalTuples: WeaponAttackOutcome['criticalTuples'] = [];
    let cascadeTriggers: CritCascadeTrigger[] = [];
    let extraDamage: ExtraDamageFragment[] = [];
    let damageAdjustments: DamageAdjustment[] = [];
    let damageFeedback: DamageFeedback | null = null;
    let casterHealing = 0;
    if (hit) {
      const defense = read.postRollReduce?.({ ...reactionAttacker, encounter_creatures: {
        ...reactionAttacker.encounter_creatures, luck: state.luckNow, ...(state.fortunateFired ? { reaction: 0 } : {}) } }, reactionTarget, plan.rollType);
      if (defense) {
        state.targetReaction = defense.reaction;
        if (defense.vitality !== undefined) state.vNow = defense.vitality;
        if (defense.attackerLuck !== undefined && state.luckNow !== defense.attackerLuck) {
          state.luckNow = defense.attackerLuck; state.luckMoved = true;
        }
      }
      for (let i = 0; i < plan.dice; i += 1) {
        const rolled = rollWithStacks(roll, plan.die, damageAdvDisStacks);
        damageDraws.push(rolled.kept); damageDice.push(...rolled.draws);
      }
      // damage_type_is_rolled_at_max: a fragment forced to its maximum reads dice × die (the dice still draw) and never explodes
      const maxed = plan.maxedBy != null;
      const fragmentExplodes = canExplode && !maxed;
      // Only kept faces seed explosion chains; discarded advantage faces do not explode.
      const fortune = fragmentExplodes ? attackerFortune : 0;
      if (fragmentExplodes) {
        for (const face of damageDraws) damageExplosions.push(...explosionChain(roll, face, plan.die, fortune));
      }
      const baseResult = (maxed ? plan.dice * plan.die : damageDraws.reduce((total, face) => total + face, 0))
        + damageExplosions.reduce((total, link) => total + link.value, 0)
        + swingDamageEssence;
      if (critical) {
        // Crit: Doubled reads the fragment result AS IT STANDS at the hook — dice, explosions, modifier
        // and essence all included — which is why the bonus is computed from `baseResult`, not from the
        // dice alone.
        const bonus = criticalHitBonus(roll, variant, { dice: plan.dice, die: plan.die, rollResult: baseResult },
          { canExplode: fragmentExplodes, fortune, multiplier: plan.criticalMultiplier });
        criticalVariant = bonus.variant;
        criticalBonus = bonus.total;
        criticalTuples = bonus.tuples.map(t => ({ value: t.value, label: t.label, kind: t.kind }));
        cascadeTriggers = resolveCritCascades(roll, cascadeRules, plan.dice, plan.die);
      }
      // The fragment result as it stands at the mitigation call site (damage_type_roll.php:1033): dice, explosions, the
      // crit hook and the essence (folded into roll_mod) are all in. Mitigation then halves / zeroes / doubles it in
      // order, and the fragment floors at 0 (line 1057) whether or not anything matched.
      damageBeforeMitigation = baseResult + criticalBonus + cascadeTriggers.reduce((sum, trigger) => sum + trigger.bonus, 0);
      const weaponFragment = mitigateFragment(damageBeforeMitigation, plan.mitigation);
      // S10: the extra fragments roll AFTER the weapon's (its crit cascades included), in damage_type_rolls() order, each gated
      // on this swing's outcome: a crit-only fragment needs the critical; an at-advantage one needs the attack at net advantage.
      extraDamage = extraPlans
        .filter(p => (p.when === 'always' || critical) && (!p.requiresAdvantage || stacks > 0))
        .map(p => rollExtraFragment(roll, { ...p, maxedBy: maxTypes.get(upper(p.damageTypeId)) ?? null },
          { critical, variant, canExplode, fortune: canExplode ? attackerFortune : 0, multiplier: plan.criticalMultiplier }));
      const fragments = [
        { damageTypeId: plan.damageTypeId, beforeMitigation: damageBeforeMitigation, result: weaponFragment.result, zeroReason: weaponFragment.zeroReason,
          healsCaster: read.entries(plan.weapon, 'damage_heals_caster').length > 0 },
        ...extraDamage,
      ];
      const preMitigation = fragments.some(fragment => fragment.result > 0) ? read.preMitigation?.(
        { ...reactionAttacker, encounter_creatures: { ...reactionAttacker.encounter_creatures, luck: state.luckNow,
          ...(state.fortunateFired ? { reaction: 0 } : {}) } },
        { ...reactionTarget, encounter_creatures: { ...reactionTarget.encounter_creatures, reaction: state.targetReaction } },
        [...new Set(fragments.map(fragment => fragment.damageTypeId))]) : undefined;
      if (preMitigation) {
        state.targetReaction = preMitigation.reaction;
        state.vNow = preMitigation.vitality;
        if (state.luckNow !== preMitigation.attackerLuck) { state.luckNow = preMitigation.attackerLuck; state.luckMoved = true; }
      }
      const totals = totalDamage(fragments, { ...pipeline, ...(defense ? { reductions: defense.reductions } : {}),
        ...(preMitigation ? { damageMitigation: preMitigation.damageMitigation, reductions: preMitigation.reductions } : {}), mitigationIgnored: plan.ignoresDm,
        ecDamageReduction: !critical && result < swingFullAc ? damageReduction : 0 }, roll);
      damage = totals.total;
      casterHealing = totals.casterHealing;
      damageAdjustments = totals.adjustments;
      damageFeedback = totals.feedback;
      for (const reason of pipeline.skippedEffects ?? []) skippedEffects.add(reason);
      lastAppliedDamage = shifted ? read.absorbFormDamage!(target, damage) : damage;
      state.vNow -= lastAppliedDamage;
    }
    return { d20, stacks, sources, reactionFired, luckSpent, tierDraw, modifier, result, hit, autoMiss, critical, criticalVariant, criticalBonus, criticalTuples, cascadeTriggers, damageDraws, damageDice, damageAdvDisStacks, damageExplosions, damage, damageBeforeMitigation, extraDamage, casterHealing, damageAdjustments, damageFeedback };
  };

  // THE PAID SWING: Body on the attack modifier AND on the damage. An actual automatic Fortunate gain is carried to the rider.
  const first = swing(paid, essence, damageEssence, fortunateOnGain, pipeline.plans, strayReroll ? 0 : int(read.revealedAttackAdv), nextRollAgainstSources);
  // Each critical saves before the next hand, carrying the target's luck and Maimed penalty.
  // Lane A8: the target's own Evade against the weapon's roll type (skill_roll_arr is_target) — maimedEvadeSource.
  const maimedSave = first.critical && saveInput ? resolveMaimedSave(roll, saveInput, int(targetEc.luck), input.weaponId,
    maimedEvadeSource(first.reactionFired === EVADE_ID, state.targetReaction, targetInCombat, maimedEvadeEnabled), luckActive) : null;
  const paidDc = dc;
  let targetFortunate = false;
  let settledDeath: WeaponAttackOutcome['death'] = null, stopped = false;
  const settle = (hit: SwingResult, plan: WeaponPlan, maimed: MaimedSave | null): void => {
    if (!read.settleSwing) return;
    const result = read.settleSwing({ hit: hit.hit, paid: plan === paid, hasRider: plan === paid && !!rider, targetId: input.targetId, rollType: plan.rollType,
      postRollFinal: !(deferNaturalOne && hit.d20.kept === 1), weaponId: plan.weaponId, damageTypes: [plan.damageTypeId, ...hit.extraDamage.map(d => d.damageTypeId)],
      damage: hit.damage ?? 0, casterHealing: hit.casterHealing, critical: hit.critical, vitality: state.vNow, luck: state.luckNow,
      reaction: state.fortunateFired || opportunityAttack ? 0 : int(attackerEc.reaction), targetReaction: state.targetReaction,
      aps: attackerInCombat ? aps - (opportunityAttack ? 0 : ATTACK_AP_COST) : aps, before: state.vNow + (hit.hit ? lastAppliedDamage ?? 0 : 0),
      nextRollAgainstConsumed: plan === paid ? nextRollAgainstConsumed : [], maimedSave: maimed });
    state.vNow = result.vitality;
    if (state.luckNow !== result.luck) state.luckMoved = true;
    state.luckNow = result.luck; state.targetReaction = result.targetReaction;
    targetFortunate = result.targetFortunate === true;
    settledDeath = result.death; stopped = result.stop;
  };
  settle(first, paid, maimedSave);
  // Arithmetic-only callers still retire a free hand on the paid hit's actual terminal outcome.
  // Runtime effects remain entirely owned by settleSwing's shared frame.
  if (rider && !read.settleSwing && first.hit && first.damage !== null && state.vNow <= 0) {
    stopped = !isPlayerCharacter || first.damage >= vMax;
    settledDeath = stopped ? (isPlayerCharacter ? 'massive_damage' : 'killed') : 'dying';
    state.vNow = 0;
  }
  if (rider && read.settleSwing && !stopped && read.partnerWeapon) {
    const reason = partnerPlan(paidHandForPartner);
    if (reason) return no(reason);
    if (!riderPlan) { rider = null; riderPipeline = null; }
  }
  if (rider && read.settleSwing && !stopped) {
    const nextDc = read.armorClass(target);
    if (nextDc === null || !Number.isFinite(nextDc)) return no('target-ac-unavailable');
    dc = nextDc;
    const nextPlan = planWeapon(riderPlan?.weaponId ?? rider.weaponId, riderPlan?.weapon ?? rider.weapon, riderPlan?.explicitInventoryWeapon === true);
    if ('reason' in nextPlan) return no('rider-' + nextPlan.reason);
    rider = { ...nextPlan, label: rider.label, maxedBy: maxTypes.get(upper(nextPlan.damageTypeId)) ?? null };
    const nextPipeline = pipelineOf(rider.weapon);
    if (!nextPipeline.supported) return no('rider-' + nextPipeline.reason);
    if (nextPipeline.plans.length > 0 && rider.dice + nextPipeline.plans.reduce((sum, p) => sum + p.dice, 0) > 64) return no('damage-dice-budget-out-of-scope');
    riderPipeline = nextPipeline;
    if (read.attackSpatial) riderSpatial = read.attackSpatial(attacker, target, rider.weapon, { melee: rider.rollType === WEAPON_MELEE_ATTACK_ROLL, throwing: false });
  }
  if (riderPipeline) pipeline = riderPipeline;
  // The target's extra-damage-taken rolls once per action: the rider skips each grant (ability id | granter) the paid hit rolled.
  const takenRolled = new Set(first.extraDamage.map(fragment => fragment.grantKey).filter(Boolean));
  const riderPlans = pipeline.plans.filter(plan => !plan.grantKey || !takenRolled.has(plan.grantKey));
  // THE RIDER (plan §20.1 step 3): Body on the attack modifier, NONE on the damage; no AP, no counter, no head flag. It is the
  // LAST roll of the action, so a rider natural 20 that gains luck fires universal Fortunate exactly like a single swing (the S8
  // tail applies the status after the action). A paid-swing grant has already spent the reaction and cannot fire again.
  // OWNER RULING 90 (2026-10-16): Evade dodges ONE attack, the paid (first) swing — whichever hand was clicked; the free rider swing of
  // the other hand (natural weapons, and two-weapon fighting) never triggers it. In combat the spent reaction already ensured that;
  // out of combat the reaction is not spent, so it is cleared here.
  targetHasEvade = false;
  const liveRiderSave = rider && !stopped && read.settleSwing && cascadeRules.maimed && !targetFortunate
    ? read.fortitudeSave?.(target, rider.weapon, []) : riderSaveInput;
  if (rider && !stopped && cascadeRules.maimed && !liveRiderSave) return no('maimed-save-input-unavailable');
  const second = rider && !stopped ? swing(rider, int(read.essenceBonus(attacker, rider.weapon)), 0, fortunateOnGainRider, riderPlans, 0, [], !read.settleSwing && settledDeath === 'dying') : null;
  // Only an actual second critical would consume the target's newly granted next-roll status.
  if (second?.critical && targetFortunate && cascadeRules.maimed) return no('maimed-fortunate-trigger-out-of-scope');
  const carriedRiderSave = liveRiderSave ? afterMaimedAscension(liveRiderSave, maimedSave) : null;
  const secondSaveInput = carriedRiderSave && !read.settleSwing ? { ...carriedRiderSave,
    globalMod: carriedRiderSave.globalMod - Number(!!maimedSave?.failed) - Number(settledDeath === 'dying') } : carriedRiderSave;
  const riderMaimedSave = second?.critical && secondSaveInput && rider
    ? resolveMaimedSave(roll, secondSaveInput, read.settleSwing ? int(targetEc.luck) : maimedSave?.luckAfter ?? int(targetEc.luck), rider.weaponId,
      maimedEvadeSource(second.reactionFired === EVADE_ID, state.targetReaction, targetInCombat, maimedEvadeEnabled), luckActive) : null;
  if (second && rider) settle(second, rider, riderMaimedSave);

  // ---- what gets written. Only what legacy actually writes (S1 golden §3). ----
  const deltas: WeaponAttackDeltas = {
    // weapon_attack is a per-turn COUNTER (template_weapon_attack_roll: `+ 1`), so the write is the row's value
    // plus one — identical to the golden's 0 → 1 on a first swing; head_attack only for a head-slot weapon. An attacker
    // outside combat writes its row back unchanged (no AP, no counter, no head flag).
    attacker: attackerInCombat
      ? { aps: aps - (opportunityAttack ? 0 : ATTACK_AP_COST), weapon_attack: int(attackerEc.weapon_attack) + 1, ...(opportunityAttack ? { reaction: 0 as const } : {}), ...(isHeadSlot ? { head_attack: 1 as const } : {}) }
      : { aps, weapon_attack: int(attackerEc.weapon_attack) },
    target: {},
    serverFollowUp: [],
  };
  // The DELTA is keyed on the net change (no row write when the pool returns to where it started);
  // the OUTCOME reports the pool whenever it moved at all.
  if (state.luckNow !== luckBefore) deltas.attacker.luck = state.luckNow;
  if (state.fortunateFired) deltas.attacker.reaction = 0;
  const targetLuckAfter = riderMaimedSave?.luckAfter ?? maimedSave?.luckAfter;
  if (targetLuckAfter !== undefined && targetLuckAfter !== int(targetEc.luck)) deltas.target.luck = targetLuckAfter;

  // ---- the death tail ----
  // ⚠ THE WHOLE CHAIN IS INSIDE ONE ZERO-VITALITY GATE. `apply_damage_to_target_inc.php:358` opens with
  // `if ($target_v_current <= 0)` and EVERY death branch — massive damage included — lives inside it.
  // An earlier draft tested `damage >= v_max` first, which killed a target that had survived: at 15
  // current / 10 max, a 10-damage hit leaves 5 vitality, and PHP does not enter the death chain at all.
  // Survival is therefore decided FIRST, and only then is the kind of death classified.
  // (Codex review 2026-09-07 — a lethal divergence, and reachable wherever current can exceed maximum.)
  let death: WeaponAttackOutcome['death'] = null;
  const statusesApplied: string[] = [];
  const anyHit = first.hit || Boolean(second?.hit);
  const lastDamage = shifted ? lastAppliedDamage : second?.hit && second.damage !== null ? second.damage : first.damage;
  if (anyHit && lastDamage !== null) {
    const vAfter = state.vNow;
    if (vAfter > 0) {
      deltas.target.v_current = vAfter; // survived — no death branch runs
    } else if (isPlayerCharacter && lastDamage >= vMax) {
      // MASSIVE DAMAGE: a single hit at or above the target's MAXIMUM vitality kills a player character
      // outright — no dying state (apply_damage_to_target_inc.php:435). Note it compares the DAMAGE
      // against maximum vitality, not the resulting total against zero.
      death = 'massive_damage';
      deltas.target.v_current = 0;
      deltas.target.encounter_creature_dead = 1;
    } else if (isPlayerCharacter) {
      // A player character reduced to 0 enters the DYING state rather than dying outright.
      // apply_player_dying: remove Prone, then Crippled + Dying (one connection) + Maimed, drop in-hand
      // gear, walking speed. The shared browser damage executor plans the admitted cascade before this swing draws.
      death = 'dying';
      deltas.target.v_current = 0;
      statusesApplied.push('crippled', 'dying', 'maimed');
    } else {
      // Any non-summon creature at 0 simply dies (kill_or_vanish_creature → kill_creature).
      death = 'killed';
      deltas.target.v_current = 0;
      deltas.target.encounter_creature_dead = 1;
    }
  }
  if (first.reactionFired && targetInCombat) deltas.target.reaction = 0;
  if (read.settleSwing) {
    // A miss can observe a target already at zero; it does not own a death declaration.
    death = anyHit ? settledDeath : null;
    statusesApplied.splice(0, statusesApplied.length, ...(death === 'dying' ? ['crippled', 'dying', 'maimed'] : []));
    if (anyHit) deltas.target.v_current = Math.max(0, state.vNow);
    if (death === 'killed' || death === 'massive_damage') deltas.target.encounter_creature_dead = 1;
    else delete deltas.target.encounter_creature_dead;
  }
  if (shifted) deltas.massiveLog = death === 'killed' && lastDamage !== null && lastDamage >= vMax;

  if (death === 'killed' || death === 'massive_damage') {
    deltas.serverFollowUp.push('queue_combat_exit_check');
  }

  const drawsOf = (s: SwingResult, plan: WeaponPlan): Array<{ die: number; value: number }> => [
    ...s.d20.draws.map(d => ({ die: 20, value: d.value })),
    ...(s.tierDraw !== null && plan.tierDie ? [{ die: plan.tierDie, value: s.tierDraw }] : []),
    ...s.damageDice.map(({ value }) => ({ die: plan.die, value })),
    ...s.damageExplosions.map(link => ({ die: plan.die, value: link.value })),
    ...s.criticalTuples.map(t => ({ die: plan.die, value: t.value })),
    ...s.cascadeTriggers.map(t => ({ die: 20, value: t.face })),
    ...s.extraDamage.flatMap(f => [
      ...f.draws.map(value => ({ die: f.die, value })),
      ...f.explosions.map(link => ({ die: f.die, value: link.value })),
      ...f.criticalTuples.map(t => ({ die: f.die, value: t.value })),
    ]),
  ];
  const saveDraws = (save: MaimedSave | null, input: FortitudeSaveInput | null | undefined): Array<{ die: number; value: number }> => !save ? [] : [
    ...save.row.rolls_full.filter(t => /^d20|^Skill Roll d/.test(t[1]))
      .map((t, i) => ({ die: t[1].startsWith('d20') ? 20 : input!.tierDie!, value: Number(t[0]), order: Number(t[2]?.roll_order ?? i + 1) }))
      .sort((a, b) => a.die === 20 && b.die === 20 ? a.order - b.order : 0)
      .map(({ die, value }) => ({ die, value })),
    ...(save.ascensionFace != null ? [{ die: 20, value: save.ascensionFace }] : []),
  ];

  return {
    supported: true,
    outcome: {
      ...(skippedEffects.size ? { skippedEffects: [...skippedEffects] } : {}),
      ...(nextRollAgainstConsumed.length ? { nextRollAgainstConsumed } : {}),
      ...(opportunityAttack ? { opportunityAttack: true as const } : {}),
      naturalD20: first.d20.kept,
      d20Draws: first.d20.draws,
      advDisStacks: first.stacks,
      advDisSources: first.sources,
      skillTierDraw: first.tierDraw,
      ...(paid.skillModifiers && int(read.skillRollMod(attacker, weapon)) ? { skillRollMod: int(read.skillRollMod(attacker, weapon)) } : {}),
      ...(explicitInventory && int(read.value(weapon, 'weapon_attack_mod')) ? { weaponAttackMod: int(read.value(weapon, 'weapon_attack_mod')) } : {}),
      skillTierDie: paid.tierDie ?? null,
      modifier: first.modifier,
      result: first.result,
      dc: paidDc,
      fullAc,
      hit: first.hit,
      autoMiss: first.autoMiss,
      critical: first.critical,
      criticalVariant: first.criticalVariant,
      criticalBonus: first.criticalBonus,
      cascadeTriggers: first.cascadeTriggers,
      maimedSave,
      fortunateFired: state.fortunateFired,
      ...(state.interSwingFortunate ? { interSwingFortunate: true } : {}),
      criticalTuples: first.criticalTuples,
      damageDraws: first.damageDraws,
      ...(first.damageAdvDisStacks ? { damageDice: first.damageDice, damageAdvDisStacks: first.damageAdvDisStacks } : {}),
      damageExplosions: first.damageExplosions,
      damageMaxedBy: first.hit ? paid.maxedBy ?? null : null,
      damage: first.damage,
      damageBeforeMitigation: first.damageBeforeMitigation,
      extraDamage: first.extraDamage,
      damageAdjustments: first.damageAdjustments,
      damageFeedback: first.damageFeedback,
      mitigation: paid.mitigation,
      reactionFired: first.reactionFired,
      untrained: paid.untrained,
      evadeBlockedByStatus,
      death,
      statusesApplied,
      luckSpent: first.luckSpent,
      luckAfter: state.luckMoved ? state.luckNow : null,
      rider: second && rider ? {
        rollType: rider.rollType,
        ...(rider.skillModifiers && int(read.skillRollMod(attacker, rider.weapon)) ? { skillRollMod: int(read.skillRollMod(attacker, rider.weapon)) } : {}),
        ...(rider.skillModifiers && int(read.value(rider.weapon, 'weapon_attack_mod')) ? { weaponAttackMod: int(read.value(rider.weapon, 'weapon_attack_mod')) } : {}),
        maimedSave: riderMaimedSave,
        weaponId: rider.weaponId, label: rider.label,
        naturalD20: second.d20.kept, d20Draws: second.d20.draws, advDisStacks: second.stacks, advDisSources: second.sources,
        skillTierDraw: second.tierDraw, skillTierDie: rider.tierDie ?? null, modifier: second.modifier, result: second.result, dc,
        hit: second.hit, autoMiss: second.autoMiss, critical: second.critical, criticalVariant: second.criticalVariant, criticalBonus: second.criticalBonus,
        cascadeTriggers: second.cascadeTriggers, criticalTuples: second.criticalTuples, damageDraws: second.damageDraws, damageExplosions: second.damageExplosions,
        ...(second.damageAdvDisStacks ? { damageDice: second.damageDice, damageAdvDisStacks: second.damageAdvDisStacks } : {}),
        damageMaxedBy: second.hit ? rider.maxedBy ?? null : null,
        damage: second.damage, damageBeforeMitigation: second.damageBeforeMitigation, mitigation: rider.mitigation, extraDamage: second.extraDamage,
        damageAdjustments: second.damageAdjustments, damageFeedback: second.damageFeedback, untrained: rider.untrained, luckSpent: second.luckSpent,
        dice: rider.dice, die: rider.die, damageTypeId: rider.damageTypeId,
      } : null,
    },
    deltas,
    // NOTE: `draws` is a convenience view for the commit. It is a RECONSTRUCTION, so a test that wants
    // to prove what was really taken should wrap the source (see recordingRollSource) rather than trust
    // this — a Codex review caught exactly that gap. The Crit: Rolled FRACTION is not representable
    // here, which is itself a reason to prefer the recording wrapper at the call site.
    draws: [
      ...drawsOf(first, paid),
      ...saveDraws(maimedSave, saveInput),
      ...(second && rider ? drawsOf(second, rider) : []),
      ...saveDraws(riderMaimedSave, secondSaveInput),
    ],
  };
}
